import { useContext, useEffect, useRef, useState } from "react";

import {
  ArrowBack,
  ArrowUpward,
  AutoFixHighRounded,
  Block,
  CheckCircleOutline,
  ContentCopy,
  DataObject,
  Delete,
  FileDownload,
  Info,
  LockOpen,
  Lock,
  PostAdd,
  Share,
  Summarize,
  Transform,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  TextField,
  Link,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  PostSubmissionSupplementalStatusRequest,
  GetSubmissionResponsePortal,
  GetSurveyResponse,
  LastUpdatedBy,
  OrgConfigModel,
  SubmissionPortal,
  SubmissionSummary,
  Template,
  ViewerDto,
  getOrgConfigPortal,
  getOrgTemplates,
  getSubmissionSynopsisPortal,
  getSubmissionViewers,
  getSubmissionXmlGenerationTestPortal,
  patchSubmissionPortal,
  patchSubmissionViewingEditing,
  postSubmissionExportEvidencePortal,
  postSubmissionGenerateIncidentPortal,
  postSubmissionStatusPortal,
  postSubmissionSupplementalStatus,
  postSubmissionClearExportPortal,
  postSubmissionSynopsisVerifiedPortal,
  postSubmissionCreateBulletinUrl,
  postSubmissionViewingJoin,
  deleteIntelSubmission,
  deleteSubmissionViewingLeave,
  SurveyConfiguration,
  getOrgSurveyConfigPortal,
  AssignedTo,
  getSubmissionAssignableUsers,
} from "@rubi/api-generated";
import {
  colors,
  ConfirmDialog,
  PageHeader,
  PotentialDuplicateWarningDialog,
} from "@rubi/ui";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { Model } from "survey-core";

import BottomFixedActionContainer from "@/common/BottomFixedActionContainer";
import {
  describeIncidentNumberFormat,
  getIncidentNumberFormat,
  validateIncidentNumber,
} from "@/common/incidentNumberFormat";
import { getServiceConfig } from "@/common/serviceConfigurations";
import { formatDateShort, mailTo } from "@/common/utils";
import { bulletinFromReportPath } from "@/components/alerts/add-v2/routePaths";
import { DisabledComponentDialog } from "@/components/DisabledComponent";
import { OnlineReportingContext } from "@/components/online-reporting/context";
import { registerAutocomplete } from "@/components/online-reporting/custom-questions/AddressAutocompleteQuestion/AddressAutocompleteQuestion";
import { registerCameraRegistryMap } from "@/components/online-reporting/custom-questions/CameraRegistryQuestion/CameraRegistryQuestion";
import { registerCurrencyInput } from "@/components/online-reporting/custom-questions/CurrencyInputQuestion/CurrencyInputQuestion";
import {
  FilePreviewConfigContext,
  useFilePreviewConfig,
} from "@/components/online-reporting/custom-questions/CustomQuestionFile";
import { registerDriversLicenseScanner } from "@/components/online-reporting/custom-questions/DriversLicenseScannerQuestion";
import { registerEnhancedFileUpload } from "@/components/online-reporting/custom-questions/EnhancedFileUploadQuestion/EnhancedFileUploadQuestion";
import { registerGasQuantityInput } from "@/components/online-reporting/custom-questions/GasQuantityInputQuestion/GasQuantityInputQuestion";
import { registerNarrativeCanvas } from "@/components/online-reporting/custom-questions/NarrativeCanvasQuestion/NarrativeCanvasQuestion";
import { extractLocaleStringFromField } from "@/components/online-reporting/form-management/utils";
import { ILocaleObject } from "@/components/online-reporting/types";
import { buildReviewerDetailsModel } from "@/components/online-reporting/utils";
import { Permission } from "@/components/UserManagement/types";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import { APP_OR_SERVER_URL } from "@/environment";
import { useNavigationGuard } from "@/hooks/useNavigationGuard";
import {
  ReportStatus,
  reportColourTypes,
  reportStatusTooltips,
  statusTypes,
} from "@/types/typesReportV2";

import AssignedToSelector from "./AssignedToSelector";
import styles from "./CondensedReportView.module.scss";
import CrimeCodesCard from "./CrimeCodesCard";
import EmailNotesCard from "./EmailNotesCard";
import ImageMatchBanner from "./ImageMatchBanner";
import { findIncidentNumberDuplicates } from "./incidentNumberDuplicateCheck";
import LinkSupplementaryDropdown from "./LinkSupplementaryDropdown";
import ActionModal from "./modals/ActionModal";
import ButtonInfoDialog from "./modals/ButtonInfoDialog";
import { ConvertToSupplementaryDialog } from "./modals/ConvertToSupplementaryDialog";
import { DeleteSubmissionDialog } from "./modals/DeleteSubmissionDialog";
import { EditConfirmationModal } from "./modals/EditConfirmationModal";
import IncidentNumberDialog from "./modals/IncidentNumberDialog";
import { LinkingDialog } from "./modals/LinkingDialog";
import { NoLinkFoundDialog } from "./modals/NoLinkFoundDialog";
import SupplementaryReportModal from "./modals/SupplementaryReportModal";
import { UnlinkAllSupplementaryReportsDialog } from "./modals/UnlinkAllSupplementaryReportsDialog";
import NotesSidePanel, {
  NOTES_PANEL_MIN_WIDTH,
  wouldNotesPanelTakeOver,
} from "./NotesSidePanel";
import type { NotesSidebarTab } from "./NotesSidePanel/NotesSidePanel.types";
import RenderCondensedReport from "./RenderCondensedReport";
import ReviewerDetails from "./ReviewerDetails";
import { useCurrentSurveyConfig } from "./useCurrentSurveyConfig";
import {
  detailsDifferFromSaved,
  getReportTitle,
  isReportClosed,
  withSupplementalDraft,
} from "./utils";
import type { SupplementalNotesDraft, SupplementalNotesDrafts } from "./utils";

const CondensedReportView = ({
  cisoDisplay = false,
  idOverrride = "",
  onSubmissionDeleted,
}: {
  cisoDisplay: boolean;
  idOverrride?: string;
  onSubmissionDeleted?: () => void;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("reportsv2/condensed_report");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { isDevEnv } = useDevEnvironment();
  const { currentUser, getOrgIDFromURL } = useContext(AuthStateContext);

  const userHasBulletinCreatePermission = currentUser?.permissions.some(
    (permission) => permission.title === Permission.ALERT_CREATE_BULLETIN,
  );

  const canViewIndividualReport = currentUser?.permissions.some(
    (permission) =>
      permission.title === Permission.REPORT_V2_VIEW_INDIVIDUAL_REPORT ||
      permission.title === Permission.REPORT_V2_VIEW_REPORTS,
  );

  const canUpdateReports = currentUser?.permissions.some(
    (permission) => permission.title === Permission.REPORT_V2_UPDATE_REPORTS,
  );

  const canDeleteEntriesIntel = currentUser?.permissions.some(
    (permission) => permission.title === Permission.INTEL_DELETE_ENTRIES,
  );

  const [deleteSubmissionDialogOpen, setDeleteSubmissionDialogOpen] =
    useState(false);
  const [isDeletingSubmission, setIsDeletingSubmission] = useState(false);

  const [reportMetadata, setReportMetadata] = useState(
    {} as GetSubmissionResponsePortal,
  );

  const { reports, getSubmissionForID, getSurveyForID, fetchLatestReports } =
    useContext(OnlineReportingContext);

  const [assignableUsers, setAssignableUsers] = useState<AssignedTo[]>([]);

  const appName = window.location.pathname.split("/")[1] ?? "";
  const { id, orgId } = useParams();
  // idToUse is the report id
  const idToUse = idOverrride || id;

  useEffect(() => {
    let cancelled = false;
    async function fetchAssignableUsers() {
      if (!idToUse) return;
      try {
        const res = await getSubmissionAssignableUsers({
          path: { submission_id: idToUse },
        });
        if (!cancelled) setAssignableUsers(res.data?.users ?? []);
      } catch {
        if (!cancelled) {
          setAssignableUsers([]);
          toast.error("Couldn't load who can be assigned to this report.");
        }
      }
    }
    fetchAssignableUsers();
    return () => {
      cancelled = true;
    };
  }, [idToUse]);

  const incidentNumberRef = useRef<HTMLInputElement>();
  const priorityRef = useRef<HTMLInputElement>();
  const [tags, setTags] = useState<string[]>([]);

  const additionalNotesRef = useRef<HTMLInputElement>();
  const [notes, setNotes] = useState<{
    approve: string;
    reject: string;
    escalate: string;
    reopen: string;
  }>({
    approve: "",
    reject: "",
    escalate: "",
    reopen: "",
  });
  const synopsisRef = useRef<HTMLInputElement>();

  const [modified, setModified] = useState(false);
  const [synopsisVerified, setSynopsisVerified] = useState(false);
  const [synopsisVerifiedLoading, setSynopsisVerifiedLoading] = useState(false);
  const [saveDetailsLoading, setSaveDetailsLoading] = useState(false);
  const [isCreatingBulletin, setIsCreatingBulletin] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalOpenSupplementary, setModalOpenSupplementary] = useState(false);
  const [selectedAction, setSelectedAction] = useState("");
  const [specifiedReason, setSpecifiedReason] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedIndex, setSelectedIndex] = useState(-1);

  const [editState, setEditState] = useState<{
    isEditMode: boolean;
    handleSave: () => Promise<void>;
    isSaving: boolean;
  } | null>(null);
  const [isEditConfirmationModalOpen, setIsEditConfirmationModalOpen] =
    useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [isSlideShowActive, setIsSlideShowActive] = useState(false);

  const [emailTemplates, setEmailTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null,
  );

  const [isSupplementaryReport, setIsSupplementaryReport] = useState(false);
  // report linking
  const [selectedReportToLink, setSelectedReportToLink] =
    useState<SubmissionSummary | null>(null);
  const [isLinkingDialogOpen, setIsLinkingDialogOpen] = useState(false);
  const [incidentNumberDialogOpen, setIncidentNumberDialogOpen] =
    useState(false);
  const [isNoLinkFoundDialogOpen, setIsNoLinkFoundDialogOpen] = useState(false);
  const [
    convertGeneralToSupplementaryDialogOpen,
    setConvertGeneralToSupplementaryDialogOpen,
  ] = useState(false);
  const [
    unlinkAllSupplementaryReportsDialogOpen,
    setUnlinkAllSupplementaryReportsDialogOpen,
  ] = useState(false);
  const [isBtnInfoDialogOpen, setIsBtnInfoDialogOpen] = useState(false);
  const [isReExportModalOpen, setIsReExportModalOpen] = useState(false);
  const [reExportLoading, setReExportLoading] = useState(false);
  const [reExportOption, setReExportOption] = useState<"rms" | "dems" | "both">(
    "rms",
  );

  const [isGeneratingSynopsis, setIsGeneratingSynopsis] = useState(false);

  const [isNotesSidePanelOpen, setIsNotesSidePanelOpen] = useState(
    () => !cisoDisplay && !isMobile && !wouldNotesPanelTakeOver(),
  );
  const [notesSidebarTab, setNotesSidebarTab] =
    useState<NotesSidebarTab>("notes");
  // Seeded from the panel's own takeover rule so first paint doesn't jitter.
  const [isPanelTakingOver, setIsPanelTakingOver] = useState(
    wouldNotesPanelTakeOver,
  );
  const [notesPanelWidth, setNotesPanelWidth] = useState(() => {
    const savedWidth = localStorage.getItem("notesPanelWidth");
    if (savedWidth) {
      const parsedWidth = parseInt(savedWidth, 10);
      if (
        !isNaN(parsedWidth) &&
        parsedWidth >= NOTES_PANEL_MIN_WIDTH &&
        parsedWidth <= 1200
      ) {
        return parsedWidth;
      }
    }
    // Default to 1/4 of screen width
    const oneQuarterWidth = window.innerWidth / 4;
    return Math.max(NOTES_PANEL_MIN_WIDTH, Math.min(oneQuarterWidth, 1200));
  });
  const getNotesPanelValuesRef = useRef<
    () => {
      notes: string;
      synopsis: string;
      incident_number: string;
    }
  >(() => ({
    notes: "",
    synopsis: "",
    incident_number: "",
  }));
  /* Unsaved supplemental notes from the inline boxes and the notes panel, kept
  here so Save and status changes can write them. */
  const [supplementalDrafts, setSupplementalDrafts] =
    useState<SupplementalNotesDrafts>({});
  const supplementalDraftsRef = useRef(supplementalDrafts);
  supplementalDraftsRef.current = supplementalDrafts;
  const hasUnsavedChanges =
    modified || Object.keys(supplementalDrafts).length > 0;

  // Report viewing presence: who is viewing (including self); is_editing from API when backend supports it
  const [viewers, setViewers] = useState<
    (ViewerDto & { is_editing?: boolean })[]
  >([]);
  const joinedSubmissionIdRef = useRef<string | null>(null);
  const concurrentEditDialogResolveRef = useRef<
    ((proceed: boolean) => void) | null
  >(null);
  const [concurrentEditDialogState, setConcurrentEditDialogState] = useState<{
    open: boolean;
    names: string;
  }>({ open: false, names: "" });

  const getEffectiveIncidentNumber = (): string => {
    const notesPanelValues = getNotesPanelValuesRef.current?.();
    if (isNotesSidePanelOpen && notesPanelValues) {
      return notesPanelValues.incident_number ?? "";
    }
    return incidentNumberRef.current?.value ?? "";
  };

  /* With auto-generation on, only prompt when this outcome exports to RMS but
     won't generate a number. An unset rms_export_outcomes means every outcome
     exports (matches api-rs). */
  const needsIncidentNumberPrompt = (action: string): boolean => {
    if (action !== "approved" && action !== "escalated") return false;
    if (getEffectiveIncidentNumber().trim()) return false;

    const metadata = serviceConfig?.metadata;
    if (!metadata?.automated_incident_number_generation) return true;

    const exportsToRms = metadata.rms_export_outcomes?.includes(action) ?? true;
    const generatesIncidentNumber =
      !!metadata.incident_generation_outcomes?.includes(action);

    return exportsToRms && !generatesIncidentNumber;
  };

  const openNotesSidePanel = () => {
    const ownDraft = supplementalDrafts[idToUse ?? ""];

    setReportMetadata((prev) => ({
      ...prev,
      // Update main report values from refs
      notes: additionalNotesRef?.current?.value ?? prev.notes ?? "",
      synopsis: synopsisRef?.current?.value ?? prev.synopsis ?? "",
      incident_number:
        incidentNumberRef?.current?.value ?? prev.incident_number ?? "",
      // If viewing a supplemental, update with unsaved inline form values
      ...(prev.is_supplemental && ownDraft ? ownDraft : {}),
    }));
    setIsNotesSidePanelOpen(true);
  };

  const closeNotesSidePanel = () => {
    // Get current values from the panel
    const values = getNotesPanelValuesRef.current();

    // Update reportMetadata with current values from panel
    setReportMetadata({
      ...reportMetadata,
      notes: values.notes,
      synopsis: values.synopsis,
      incident_number: values.incident_number,
    });

    // Sync refs with the current values
    if (incidentNumberRef?.current) {
      incidentNumberRef.current.value = values.incident_number;
    }
    if (additionalNotesRef?.current) {
      additionalNotesRef.current.value = values.notes;
    }
    if (synopsisRef?.current) {
      synopsisRef.current.value = values.synopsis;
    }
    // The inline supplemental box edits the same fields, so hand it the panel's values
    if (reportMetadata.is_supplemental) {
      setSupplementalDraft(idToUse ?? "", {
        notes: values.notes,
        synopsis: values.synopsis,
      });
    }

    setIsNotesSidePanelOpen(false);
    setNotesSidebarTab("notes");
  };

  const toggleNotesSidePanel = () => {
    if (isNotesSidePanelOpen) {
      closeNotesSidePanel();
    } else {
      setNotesSidebarTab("notes");
      openNotesSidePanel();
    }
  };

  const [currentReportIndex, setCurrentReportIndex] = useState(0);

  const handleCurrentReportChange = (reportIndex: number) => {
    setCurrentReportIndex(reportIndex);
  };

  // Persist panel width to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("notesPanelWidth", notesPanelWidth.toString());
  }, [notesPanelWidth]);

  const generateSynopsis = async () => {
    setIsGeneratingSynopsis(true);
    const response = (
      await getSubmissionSynopsisPortal({
        path: {
          submission_id: idToUse || "",
        },
      })
    ).data!;

    // Update other fields so their content is not lost if not saved when generating
    const updatedData = {
      ...reportMetadata,
      notes: additionalNotesRef?.current?.value ?? "",
      incident_number: incidentNumberRef.current?.value ?? "",
      priority: priorityRef?.current?.value
        ? parseInt(priorityRef.current.value)
        : null,
      synopsis: response.synopsis,
      tags: tags.join(","),
    };

    setReportMetadata(updatedData);
    setIsGeneratingSynopsis(false);
    setModified(true);
    setSynopsisVerified(false);
    setHasSynopsisContent(!!(response.synopsis?.trim?.() ?? ""));
  };

  const updateAssignee = async (user_id: string | null) => {
    if (!user_id) {
      patchSubmissionPortal({
        path: { submission_id: idToUse ?? "" },
        body: {
          assigned_to: {
            action: "unset",
          },
        },
      })
        .then((result) => {
          setReportMetadata({
            ...reportMetadata,
            notes: additionalNotesRef?.current?.value ?? "",
            incident_number: incidentNumberRef.current?.value ?? "",
            priority: priorityRef?.current?.value
              ? parseInt(priorityRef.current.value)
              : null,
            synopsis: synopsisRef?.current?.value ?? "",
            assigned_to: null,
          });
        })
        .catch((error) => {
          toast.error(
            "Failed to unassign report. Please contact support@tryrubicon.com for assistance.",
          );
        });
    } else {
      // Assign the report
      await patchSubmissionPortal({
        path: { submission_id: idToUse ?? "" },
        body: {
          assigned_to: {
            value: user_id,
            action: "set",
          },
        },
      })
        .then((result) => {
          setReportMetadata({
            ...reportMetadata,
            notes: additionalNotesRef?.current?.value ?? "",
            incident_number: incidentNumberRef.current?.value ?? "",
            priority: priorityRef?.current?.value
              ? parseInt(priorityRef.current.value)
              : null,
            synopsis: synopsisRef?.current?.value ?? "",
            assigned_to: result.data!.assigned_to,
          });
        })
        .catch((error) => {
          toast.error(
            "Failed to assign report. Please contact support@tryrubicon.com for assistance.",
          );
        });
    }
  };

  const organizationConfig = getServiceConfig(
    orgId ?? currentUser?.rubi_org_id ?? "NA",
    isDevEnv,
  );

  const isClosed = isReportClosed(reportMetadata);
  const isExported = !!reportMetadata?.last_export_time;

  const duplicateExcludeIds = (reportMetadata?.supplementary_submissions ?? [])
    .map((s) => s.submission_id)
    .filter((sid): sid is string => Boolean(sid));

  const [duplicateReports, setDuplicateReports] = useState<SubmissionSummary[]>(
    [],
  );
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
  const notesRevertIncidentNumberRef = useRef<(() => void) | null>(null);
  const duplicateDecisionRef = useRef<((proceed: boolean) => void) | null>(
    null,
  );

  const guardIncidentNumber = (incidentNumber: string): Promise<boolean> => {
    if (duplicateDecisionRef.current) return Promise.resolve(false);

    /* Reject a malformed number before spending a duplicate lookup on it.
       `updateIncidentNumberAndNotes` re-checks, so save paths that skip this
       guard (e.g. a status change flushing pending edits) are still covered. */
    const formatError = validateIncidentNumber(
      incidentNumber,
      incidentNumberFormat,
    );
    if (formatError) {
      setIncidentNumberError(formatError);
      toast.error(formatError);
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolve) => {
      duplicateDecisionRef.current = resolve;
      void (async () => {
        const matches = await findIncidentNumberDuplicates({
          value: incidentNumber,
          submissionId: reportMetadata?.submission_id,
          orgId,
          excludeIds: duplicateExcludeIds,
          enabled: !(isClosed || isExported),
        });
        if (matches.length === 0) {
          duplicateDecisionRef.current = null;
          resolve(true);
          return;
        }
        setDuplicateReports(matches);
        setOpenDuplicateDialog(true);
      })();
    });
  };

  useEffect(() => {
    return () => {
      duplicateDecisionRef.current?.(false);
      duplicateDecisionRef.current = null;
    };
  }, []);

  const resolveDuplicateDecision = (proceed: boolean) => {
    setOpenDuplicateDialog(false);
    duplicateDecisionRef.current?.(proceed);
    duplicateDecisionRef.current = null;
  };

  const recomputeMainDetailsModified = () => {
    setModified(
      detailsDifferFromSaved(
        {
          incidentNumber: incidentNumberRef.current?.value ?? "",
          notes: additionalNotesRef.current?.value ?? "",
          synopsis: synopsisRef.current?.value ?? "",
          priority: priorityRef.current?.value ?? "0",
          tags,
        },
        reportMetadata ?? {},
      ),
    );
  };

  const cancelDuplicateSave = () => {
    if (isNotesSidePanelOpen) {
      notesRevertIncidentNumberRef.current?.();
    } else {
      if (incidentNumberRef.current) {
        incidentNumberRef.current.value = reportMetadata?.incident_number ?? "";
      }
      setIncidentNumberError(null);
      recomputeMainDetailsModified();
    }
    resolveDuplicateDecision(false);
  };
  const [isReopen, setIsReopen] = useState(false);
  const [notifyReportingParty, setNotifyReportingParty] = useState(true);
  const [assignReopenToSelf, setAssignReopenToSelf] = useState(true);

  const [serviceConfig, setServiceConfig] = useState<OrgConfigModel | null>(
    null,
  );
  const priorityOptions = serviceConfig?.metadata.priority_options ?? [];
  const tagOptions = serviceConfig?.metadata.tag_options ?? [""];
  const requireSynopsisVerification =
    serviceConfig?.metadata?.require_synopsis_verification === true;
  const [hasSynopsisContent, setHasSynopsisContent] = useState(false);
  const enforceSynopsisVerification =
    requireSynopsisVerification && hasSynopsisContent;

  /* Agency-defined shape for manually-entered RMS incident numbers. Null when
     the agency hasn't configured one, in which case any value is accepted. */
  const incidentNumberFormat = getIncidentNumberFormat(serviceConfig?.metadata);
  const [incidentNumberError, setIncidentNumberError] = useState<string | null>(
    null,
  );

  const incidentNumberHelperText = () => {
    if (incidentNumberError) return incidentNumberError;

    const autoGenerated =
      serviceConfig?.metadata.automated_incident_number_generation;

    /* No example when the agency states its accepted shape as a bare regex —
       a raw regex is not something to put in front of an officer. */
    const example = incidentNumberFormat
      ? describeIncidentNumberFormat(incidentNumberFormat)
      : null;

    if (example) {
      const format = `Format: ${example}`;
      return autoGenerated
        ? `${format} — auto-generated if left blank`
        : format;
    }

    return autoGenerated
      ? "Auto-generated if left blank; otherwise, provide a number to override"
      : "";
  };

  // Handle closing the modal
  const handleClose = () => {
    setSpecifiedReason("");
    setModalOpen(false);
    setSelectedTemplate(null);
    setIsSubmitting(false);
    // Reset notify/reopen selections so an unchecked "Notify Reporting Party"
    // (or reopen state) doesn't carry over to the next action.
    setNotifyReportingParty(true);
    setIsReopen(false);
  };

  useEffect(() => {
    setSelectedReportToLink(null);
    setIsLinkingDialogOpen(false);
    setIsNoLinkFoundDialogOpen(false);
    setConvertGeneralToSupplementaryDialogOpen(false);
    // Reset modified flag so stale dirty state from the previous report
    // doesn't trigger an auto-save of notes/synopsis on the new report
    setModified(false);
    setSupplementalDrafts({});
  }, [idToUse]);

  // Report viewing presence: join on mount, poll every 30s, leave on unmount or when switching report
  useEffect(() => {
    if (!idToUse) {
      setViewers([]);
      return;
    }
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const submissionId = idToUse;

    // Only update viewers state when the list actually changed to avoid
    // unnecessary re-renders (the poll fires every 30 s).
    const updateViewersIfChanged = (
      next: (ViewerDto & { is_editing?: boolean })[],
    ) => {
      setViewers((prev) => {
        if (
          prev.length === next.length &&
          prev.every(
            (v, i) =>
              v.user_id === next[i].user_id &&
              v.is_editing === next[i].is_editing,
          )
        )
          return prev; // same list – skip update
        return next;
      });
    };

    (async () => {
      try {
        if (
          joinedSubmissionIdRef.current &&
          joinedSubmissionIdRef.current !== submissionId
        ) {
          try {
            await deleteSubmissionViewingLeave({
              path: { submission_id: joinedSubmissionIdRef.current },
            });
          } catch {
            // ignore leave errors (e.g. already left)
          }
          joinedSubmissionIdRef.current = null;
        }
        const res = (
          await postSubmissionViewingJoin({
            path: { submission_id: submissionId },
          })
        ).data!;
        updateViewersIfChanged(res.viewers ?? []);
        joinedSubmissionIdRef.current = submissionId;
        intervalId = setInterval(async () => {
          try {
            const poll = (
              await postSubmissionViewingJoin({
                path: { submission_id: submissionId },
              })
            ).data!;
            updateViewersIfChanged(poll.viewers ?? []);
          } catch {
            // ignore poll errors
          }
        }, 30_000);
      } catch {
        setViewers([]);
      }
    })();
    return () => {
      if (intervalId) clearInterval(intervalId);
      if (joinedSubmissionIdRef.current) {
        deleteSubmissionViewingLeave({
          path: { submission_id: joinedSubmissionIdRef.current },
        }).catch(() => {});
        joinedSubmissionIdRef.current = null;
      }
      setViewers([]);
    };
  }, [idToUse]);

  // When tab becomes visible again (e.g. after sleep), re-sync viewing state so our avatar and list are correct
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible" || !idToUse) return;
      if (joinedSubmissionIdRef.current !== idToUse) return;
      (async () => {
        try {
          const res = (
            await postSubmissionViewingJoin({
              path: { submission_id: idToUse },
            })
          ).data!;
          const next = res.viewers ?? [];
          setViewers((prev) => {
            if (
              prev.length === next.length &&
              prev.every(
                (v, i) =>
                  v.user_id === next[i].user_id &&
                  v.is_editing === next[i].is_editing,
              )
            )
              return prev;
            return next;
          });
        } catch {
          // ignore; will retry on next poll or when user interacts
        }
      })();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [idToUse]);

  // Sync editing state to server when user opens/closes edit mode or action modal (including incident number step)
  const isEditing =
    modalOpen ||
    modalOpenSupplementary ||
    incidentNumberDialogOpen ||
    editState?.isEditMode === true;
  useEffect(() => {
    if (!idToUse || joinedSubmissionIdRef.current !== idToUse) return;
    const currentUserId =
      currentUser?.id != null ? String(currentUser.id) : null;
    // Optimistic update: show editing dot on our avatar immediately
    setViewers((prev) =>
      currentUserId
        ? prev.map((v) =>
            v.user_id === currentUserId ? { ...v, is_editing: isEditing } : v,
          )
        : prev,
    );
    const submissionId = idToUse;
    (async () => {
      try {
        await patchSubmissionViewingEditing({
          path: { submission_id: submissionId },
          body: { editing: isEditing },
        });
        // Refresh viewers so server state (and other clients) stay in sync
        const res = (
          await getSubmissionViewers({ path: { submission_id: submissionId } })
        ).data!;
        setViewers(res.viewers ?? []);
      } catch {
        // Revert optimistic update by re-fetching server state
        try {
          const res = (
            await getSubmissionViewers({
              path: { submission_id: submissionId },
            })
          ).data!;
          setViewers(res.viewers ?? []);
        } catch {
          // If refetch also fails, leave optimistic state in place
        }
      }
    })();
  }, [idToUse, isEditing, currentUser?.id]);

  useEffect(() => {
    async function getConfig() {
      const response = (
        await getOrgConfigPortal({ path: { org_id: orgId ?? "" } })
      ).data!;
      setServiceConfig(response);
    }
    getConfig();
  }, []);

  useEffect(() => {
    async function getTemplates() {
      const response = (
        await getOrgTemplates({
          path: {
            org_id: getOrgIDFromURL() ?? currentUser?.rubi_org_id ?? "",
          },
        })
      ).data!;
      setEmailTemplates(response.templates);
    }
    getTemplates();
  }, [idToUse]);

  useEffect(() => {
    async function fetchData() {
      if (isDevEnv) {
        await getSubmissionXmlGenerationTestPortal({
          path: {
            submission_id: idToUse ?? "",
          },
        });
      }
    }
    fetchData();
  }, [idToUse, isSupplementaryReport]);

  useEffect(() => {
    if (isSubmitting) {
      if (
        modalDetails[selectedAction]?.detailsRequired &&
        specifiedReason === ""
      ) {
        toast.error(
          "Please provide a reason for the action before submitting.",
        );
        setIsSubmitting(false);
        return;
      }
      updateStatus(selectedAction)
        .then(() => {
          setModalOpen(false);
          setSpecifiedReason("");
          setIsSubmitting(false);
        })
        .catch(() => {
          setIsSubmitting(false);
        });
    }
  }, [isSubmitting, idToUse]);

  const [surveyResult, setSurveyResult] = useState<GetSurveyResponse>(
    {} as any,
  );

  const [reportsToRender, setReportsToRender] = useState<SubmissionPortal[]>(
    [],
  );

  function setSupplementalDraft(
    submissionId: string,
    changes: Partial<SupplementalNotesDraft> | null,
  ) {
    const saved =
      reportsToRender.find((report) => report.submission_id === submissionId) ??
      {};
    setSupplementalDrafts((prev) =>
      withSupplementalDraft(prev, submissionId, changes, saved),
    );
  }

  function applySavedSupplementalNotes(
    submissionId: string,
    notes: string,
    synopsis: string,
  ) {
    setReportsToRender((prev) =>
      prev.map((report) =>
        report.submission_id === submissionId
          ? { ...report, notes, synopsis }
          : report,
      ),
    );
    setReportMetadata((prev) => ({
      ...prev,
      supplementary_submissions: (prev.supplementary_submissions ?? []).map(
        (sub) =>
          sub.submission_id === submissionId
            ? { ...sub, notes, synopsis }
            : sub,
      ),
    }));
    // Keeps edits typed while the save was in flight
    setSupplementalDrafts((prev) =>
      withSupplementalDraft(prev, submissionId, prev[submissionId] ?? null, {
        notes,
        synopsis,
      }),
    );
  }

  /* The viewed report's own draft (a directly opened supplemental) is written
  by updateIncidentNumberAndNotes along with its other details. */
  async function saveSupplementalDrafts() {
    const pending = Object.entries(supplementalDraftsRef.current).filter(
      ([submissionId]) => submissionId !== idToUse,
    );
    const results = await Promise.allSettled(
      pending.map(async ([submissionId, draft]) => {
        await patchSubmissionPortal({
          path: { submission_id: submissionId },
          body: { notes: draft.notes, synopsis: draft.synopsis },
        });
        applySavedSupplementalNotes(submissionId, draft.notes, draft.synopsis);
      }),
    );
    if (results.some((result) => result.status === "rejected")) {
      throw new Error("Failed to save supplemental notes");
    }
  }

  // Reviewer details state
  const [reviewerDetailsSurveyModel, setReviewerDetailsSurveyModel] =
    useState<Model | null>(null);
  const [reviewerDetailsSurveyConfig, setReviewerDetailsSurveyConfig] =
    useState<SurveyConfiguration | null>(null);
  const [isSavingReviewerDetails, setIsSavingReviewerDetails] = useState(false);
  const currentSurveyConfig = useCurrentSurveyConfig(
    orgId,
    reportMetadata?.survey_class,
  );
  const [crimeCodeCount, setCrimeCodeCount] = useState(0);

  const showExportCheckbox =
    !isClosed &&
    (!!serviceConfig?.export_configuration?.rms_export_configuration ||
      !!serviceConfig?.export_configuration?.dems_export_configuration);

  const displayTagsField =
    cisoDisplay ||
    (serviceConfig?.rubi_org_id === "d718b876-5b95-49d3-93a7-c953233cd73d" &&
      surveyResult.survey?.configuration?.title ===
        "Hate Motivated Crime/Incident");

  const filePreviewConfig = useFilePreviewConfig(
    serviceConfig,
    showExportCheckbox,
  );

  const getReportData = async () => {
    if (getSubmissionForID && getSurveyForID) {
      const fetchedReportMetadata = await getSubmissionForID(idToUse ?? "");
      const fetchedSurveyResult = await getSurveyForID(
        fetchedReportMetadata?.survey_id ?? "",
      );
      setSurveyResult(fetchedSurveyResult ?? ({} as GetSurveyResponse));
      setIsSupplementaryReport(fetchedReportMetadata?.is_supplemental ?? false);
      const localSurvey = new Model(fetchedSurveyResult?.survey.configuration);
      const submission = fetchedReportMetadata?.submission;
      localSurvey.data = submission;
      // setReportSurveyJS(submission);
      setReportMetadata(
        fetchedReportMetadata ?? ({} as GetSubmissionResponsePortal),
      );
      setSynopsisVerified(!!fetchedReportMetadata?.synopsis_verified_at);
      setHasSynopsisContent(
        !!(fetchedReportMetadata?.synopsis?.trim?.() ?? ""),
      );

      if (fetchedReportMetadata) {
        // Copy before sorting: the API order (oldest first) drives S1/S2 numbering.
        const submittedAt = (report: SubmissionPortal) =>
          new Date(report.time_submitted ?? report.time_created).getTime();
        setReportsToRender([
          ...[...(fetchedReportMetadata.supplementary_submissions ?? [])].sort(
            (a, b) => submittedAt(b) - submittedAt(a),
          ),
          fetchedReportMetadata,
        ]);
      }
      // setSurveyModel(localSurvey);
    }
  };

  useEffect(() => {
    registerAutocomplete();
    registerCameraRegistryMap();
    registerCurrencyInput();
    registerDriversLicenseScanner();
    registerEnhancedFileUpload();
    registerGasQuantityInput();
    registerNarrativeCanvas();
    getReportData();
  }, [idToUse]);

  // Save reviewer details function
  const saveReviewerDetails = async (reviewerDetailsData: any) => {
    if (!idToUse) return;

    setIsSavingReviewerDetails(true);
    try {
      await patchSubmissionPortal({
        path: { submission_id: idToUse },
        body: {
          reviewer_details: reviewerDetailsData,
        },
      });
      const notesPanelValues = getNotesPanelValuesRef.current?.();
      const usePanelValues = isNotesSidePanelOpen && notesPanelValues;
      // Merge into the *current* metadata instead of replacing it with a
      // snapshot. This runs from a debounced SurveyJS callback, so a plain
      // object spread would revert anything that changed in the meantime —
      // notably `status` after a reopen, which flips `isReportClosed` back to
      // true and re-locks the whole view (hiding Save Changes with the user's
      // unsaved notes still in the box).
      //
      // The ref reads fall back to `prev` rather than "" so that a ref whose
      // input isn't mounted (e.g. the notes side panel is open) doesn't wipe
      // the saved value. An empty mounted input is still "" — the user
      // clearing a field must survive.
      setReportMetadata((prev) => ({
        ...prev,
        notes: usePanelValues
          ? notesPanelValues.notes
          : (additionalNotesRef?.current?.value ?? prev.notes ?? ""),
        incident_number: usePanelValues
          ? notesPanelValues.incident_number
          : (incidentNumberRef.current?.value ?? prev.incident_number ?? ""),
        priority: priorityRef?.current?.value
          ? parseInt(priorityRef.current.value)
          : (prev.priority ?? null),
        synopsis: usePanelValues
          ? notesPanelValues.synopsis
          : (synopsisRef?.current?.value ?? prev.synopsis ?? ""),
        tags: tags.join(","),
        reviewer_details: reviewerDetailsData,
      }));
    } catch (error) {
      console.error("Failed to save reviewer details:", error);
      toast.error("Failed to save reviewer details. Please try again.");
    } finally {
      setIsSavingReviewerDetails(false);
    }
  };

  // The reviewer-details auto-save handler is registered once, when the
  // SurveyJS model is first built, so the closure it captures would otherwise
  // be frozen at that render forever — stale `reportMetadata`, `tags`, and
  // `isNotesSidePanelOpen`. Route calls through a ref that tracks the latest
  // function so the save always sees current state.
  const saveReviewerDetailsRef = useRef(saveReviewerDetails);
  useEffect(() => {
    saveReviewerDetailsRef.current = saveReviewerDetails;
  });

  // Fetch reviewer details survey configuration
  useEffect(() => {
    const fetchReviewerDetailsConfig = async () => {
      if (!reportMetadata?.survey_id || !orgId || reviewerDetailsSurveyModel)
        return;

      try {
        const response = await getOrgSurveyConfigPortal({
          path: { org_id: orgId },
        });
        const surveys = response.data?.surveys || [];
        const survey = surveys.find(
          (survey: SurveyConfiguration) =>
            survey.survey_class === reportMetadata.survey_class,
        );
        if (survey?.reviewer_details_survey) {
          setReviewerDetailsSurveyConfig(survey);
          const reviewerDetailsModel = buildReviewerDetailsModel(
            survey.reviewer_details_survey,
            {
              data: reportMetadata.reviewer_details as Record<
                string,
                any
              > | null,
              mode: isClosed ? "display" : "edit",
            },
          );

          // Set up auto-save on value change (debounced)
          let saveTimeout: NodeJS.Timeout;
          reviewerDetailsModel.onValueChanged.add((sender) => {
            clearTimeout(saveTimeout);
            saveTimeout = setTimeout(() => {
              saveReviewerDetailsRef.current(sender.data);
            }, 1000); // Debounce for 1 second
          });

          setReviewerDetailsSurveyModel(reviewerDetailsModel);
        } else {
          setReviewerDetailsSurveyConfig(null);
          setReviewerDetailsSurveyModel(null);
        }
      } catch (error) {
        console.error("Failed to fetch reviewer details config:", error);
        setReviewerDetailsSurveyConfig(null);
        setReviewerDetailsSurveyModel(null);
      }
    };
    fetchReviewerDetailsConfig();
  }, [reportMetadata?.survey_id, reportMetadata?.reviewer_details, orgId]);

  // Update reviewer details mode when report is closed/reopened
  useEffect(() => {
    if (reviewerDetailsSurveyModel) {
      reviewerDetailsSurveyModel.mode = isClosed ? "display" : "edit";
    }
  }, [isClosed, reviewerDetailsSurveyModel]);

  useEffect(() => {
    if (incidentNumberRef?.current) {
      incidentNumberRef.current.value = reportMetadata?.incident_number ?? "";
      setIncidentNumberError(null);
    }

    if (additionalNotesRef?.current) {
      additionalNotesRef.current.value = reportMetadata?.notes ?? "";
    }

    if (priorityRef?.current) {
      priorityRef.current.value = reportMetadata?.priority?.toString() ?? "0";
    }

    if (synopsisRef?.current) {
      synopsisRef.current.value = reportMetadata?.synopsis ?? "";
    }

    setHasSynopsisContent(!!(reportMetadata?.synopsis?.trim?.() ?? ""));

    setTags(reportMetadata?.tags?.split(",").map((tag) => tag.trim()) ?? []);
    setNotes({
      approve: reportMetadata?.approve_note ?? "",
      reject: reportMetadata?.reject_note ?? "",
      escalate: reportMetadata?.escalate_note ?? "",
      reopen: reportMetadata?.reopen_note ?? "",
    });
  }, [reportMetadata, idToUse]);

  // Unsaved detail edits are guarded at the router, so this only navigates —
  // leaving via the sidebar or browser back is prompted the same way.
  function goBack() {
    navigate(`/${appName}/${orgId}/reports`);
  }

  // Converting a report re-navigates to its own URL, which is not leaving.
  const leaveGuard = useNavigationGuard(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
  );

  // Handle beforeunload warning for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        return "Your detail changes have not been submitted. Are you sure you want to leave?";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  function showConcurrentEditingWarning(names: string): Promise<boolean> {
    return new Promise((resolve) => {
      concurrentEditDialogResolveRef.current = resolve;
      setConcurrentEditDialogState({ open: true, names });
    });
  }

  async function confirmNoOtherEditors(): Promise<boolean> {
    if (!idToUse) return true;
    let freshViewers: (ViewerDto & { is_editing?: boolean })[] = [];
    try {
      const res = (
        await getSubmissionViewers({ path: { submission_id: idToUse } })
      ).data!;
      freshViewers = res.viewers ?? [];
      setViewers(freshViewers);
    } catch {
      // Use current viewers so we can still warn if refetch fails
      freshViewers = viewers;
    }
    const currentUserId =
      currentUser?.id != null ? String(currentUser.id) : null;
    const otherEditing = currentUserId
      ? freshViewers.filter((v) => v.user_id !== currentUserId && v.is_editing)
      : [];
    if (otherEditing.length === 0) return true;
    const names = otherEditing
      .map((v) => `${v.first_name} ${v.last_name}`)
      .join(", ");
    return showConcurrentEditingWarning(names);
  }

  async function actionSelected(action: string) {
    if (editState?.isEditMode) {
      setPendingAction(action);
      setIsEditConfirmationModalOpen(true);
      return;
    }

    const isReopenAction = action === "submitted" && isClosed;

    if (enforceSynopsisVerification && !synopsisVerified && !isReopenAction) {
      toast.error(
        "Please confirm you have verified the synopsis before taking this action.",
      );
      return;
    }

    // Validate reviewer details before approval/escalation
    if (
      (action === "approved" || action === "escalated") &&
      reviewerDetailsSurveyModel
    ) {
      // Save any pending changes first
      if (reviewerDetailsSurveyModel.data) {
        saveReviewerDetails(reviewerDetailsSurveyModel.data);
      }

      // Check for validation errors
      const hasErrors = reviewerDetailsSurveyModel.hasErrors(true, false);
      if (hasErrors) {
        toast.error(
          "Please complete all mandatory fields in Reviewer Details before " +
            (action === "approved" ? "approving" : "escalating") +
            " the record.",
        );
        return;
      }
    }

    if (
      (action === "approved" || action === "escalated") &&
      serviceConfig?.metadata.enable_crime_codes &&
      currentSurveyConfig?.mandatory_crime_codes &&
      crimeCodeCount === 0
    ) {
      toast.error(
        "Please assign at least one crime code before approving the record.",
      );
      return;
    }

    setSelectedAction(action);

    // Warn if another viewer is currently editing
    if (!(await confirmNoOtherEditors())) {
      setSelectedAction("");
      return;
    }

    if (action === "closeSupplementary") {
      setModalOpenSupplementary(true);
      return;
    }

    if (needsIncidentNumberPrompt(action)) {
      setIncidentNumberDialogOpen(true);
      return;
    }

    setModalOpen(true);
    setModalOpenSupplementary(false);
  }

  const handleSaveAndContinue = async () => {
    if (!editState?.handleSave || !pendingAction) return;

    try {
      await editState.handleSave();
      setIsEditConfirmationModalOpen(false);
      // Proceed with the pending action
      const action = pendingAction;
      setPendingAction(null);
      setSelectedAction(action);

      // Warn if another viewer is currently editing
      if (!(await confirmNoOtherEditors())) {
        setSelectedAction("");
        return;
      }

      if (action === "closeSupplementary") {
        setModalOpenSupplementary(true);
        return;
      }

      if (needsIncidentNumberPrompt(action)) {
        setIncidentNumberDialogOpen(true);
        return;
      }

      setModalOpen(true);
      setModalOpenSupplementary(false);
    } catch (error) {
      // Error is already handled in handleSave, just keep modal open
      console.error("Failed to save before continuing:", error);
    }
  };

  const handleCancelEditConfirmation = () => {
    setIsEditConfirmationModalOpen(false);
    setPendingAction(null);
  };

  async function linkReport(selectedReport: SubmissionSummary) {
    try {
      const originalReportId = selectedReport.id;
      const payload: PostSubmissionSupplementalStatusRequest = {
        is_supplemental: true,
        original_id: originalReportId,
      };
      await postSubmissionSupplementalStatus({
        path: { submission_id: idToUse ?? "" },
        body: payload,
      });
      toast.success("Report linked successfully");
      // The link is already persisted; blocking here would only strand the user
      // on a stale page.
      leaveGuard.allowNextNavigation();
      navigate(`/${appName}/${orgId}/report/${originalReportId}/condensed`, {
        replace: true,
      });
      setIsLinkingDialogOpen(false);
    } catch (error) {
      console.error("Error while linking report", error);
    }
  }

  async function unlinkReport(reportSubmissionId: string) {
    try {
      const payload: PostSubmissionSupplementalStatusRequest = {
        is_supplemental: true,
        original_id: null,
      };
      await postSubmissionSupplementalStatus({
        path: { submission_id: reportSubmissionId },
        body: payload,
      }).then(() => {
        toast.success("Report unlinked successfully");
        leaveGuard.allowNextNavigation();
        navigate(
          `/${appName}/${orgId}/report/${reportSubmissionId}/condensed`,
          { replace: true },
        );
      });
    } catch (error) {
      console.error("Error while unlinking report ", error);
    }
  }

  async function convertToGeneralReport() {
    try {
      const payload: PostSubmissionSupplementalStatusRequest = {
        is_supplemental: false,
      };
      await postSubmissionSupplementalStatus({
        path: { submission_id: idToUse ?? "" },
        body: payload,
      }).then(async () => {
        toast.success("Report converted successfully");
        await getReportData();
        navigate(`/${appName}/${orgId}/report/${idToUse}/condensed`, {
          replace: true,
        });
      });
    } catch (error) {
      console.error("Error while converting report", error);
    }
  }

  async function convertToSupplementaryReport() {
    try {
      const payload: PostSubmissionSupplementalStatusRequest = {
        is_supplemental: true,
      };
      await postSubmissionSupplementalStatus({
        path: { submission_id: idToUse ?? "" },
        body: payload,
      }).then(async () => {
        toast.success("Report converted successfully");
        await getReportData();
        setConvertGeneralToSupplementaryDialogOpen(false);
        navigate(`/${appName}/${orgId}/report/${idToUse}/condensed`, {
          replace: true,
        });
      });
    } catch (error) {
      console.error("Error while converting report", error);
    }
  }

  const downloadPDF = async () => {
    let backend = APP_OR_SERVER_URL;
    if (organizationConfig?.apiRegion === "us") {
      backend = backend.replace("prod.api", "us.api");
    }
    window.open(
      `${backend}/report/portal/submission/${idToUse}/pdf?combined=true`,
      "_self",
    );
  };

  interface IModalDetails {
    title: string;
    description: string | JSX.Element;
    detailsRequired: boolean;
    detailsText: string;
    rteOptionalText?: string;
  }

  const modalDetails: { [key: string]: IModalDetails } = {
    edit: {
      title: t("edit_report_title"),
      description: t("edit_report_description"),
      detailsRequired: false,
      detailsText: "",
    },
    modification_requested: {
      title: t("modification_requested_title"),
      description: (
        <>
          <b>{t("modification_requested_description_bold")}</b>
          {t("modification_requested_description_normal")}
        </>
      ),
      detailsRequired: true,
      detailsText: t("modification_requested_details_text"),
      rteOptionalText: t("modification_requested_rte_optional_text"),
    },
    approved: {
      title: t("approve_report_title"),
      description: (
        <>
          <b>{t("approve_report_description_bold")}</b>
          {t("approve_report_description_normal")}
        </>
      ),
      detailsRequired: false,
      detailsText: t("approve_report_details_text"),
    },
    rejected: {
      title: t("rejected_report_title"),
      description: (
        <>
          <b>{t("rejected_report_description_bold")}</b>
          {t("rejected_report_description_normal")}
        </>
      ),
      detailsRequired: true,
      detailsText: t("rejected_report_details_text"),
    },
    escalated: {
      title: t("escalate_report_title"),
      description: (
        <>
          <b>{t("escalate_report_description_bold")}</b>
          {t("escalate_report_description_normal")}
        </>
      ),
      detailsRequired: true,
      detailsText: t("escalate_report_details_text"),
    },
    // This is used as reopen since the submitted action is being used for reopen
    submitted: {
      title: t("reopen_report_title"),
      description: <> {t("reopen_report_description")} </>,
      detailsRequired: false,
      detailsText: t("reopen_report_details_text"),
    },
    closeSupplementary: {
      title: t("close_supplementary_report_title"),
      description: t("close_supplementary_report_description"),
      detailsRequired: false,
      detailsText: t("close_supplementary_report_details_text"),
    },
  };

  if (organizationConfig?.disableModificationRequest) {
    delete modalDetails.modification_requested;
  }

  async function updateIncidentNumberAndNotes(overrideValues?: {
    incident_number?: string;
    notes?: string;
    synopsis?: string;
  }) {
    if (enforceSynopsisVerification && !synopsisVerified) {
      toast.error(
        "Please confirm you have verified the synopsis before saving.",
      );
      setSaveDetailsLoading(false);
      throw new Error("Synopsis verification required");
    }
    setSaveDetailsLoading(true);
    let updatedData = reportMetadata;
    const notesPanelValues = getNotesPanelValuesRef.current?.();
    const ownDraft = supplementalDraftsRef.current[idToUse ?? ""];

    let incidentNumberValue = "";

    if (overrideValues?.incident_number !== undefined) {
      incidentNumberValue = overrideValues.incident_number;
    } else if (
      isNotesSidePanelOpen &&
      notesPanelValues?.incident_number !== undefined
    ) {
      incidentNumberValue = notesPanelValues.incident_number;
    } else if (incidentNumberRef?.current?.value) {
      incidentNumberValue = incidentNumberRef.current.value;
    }
    incidentNumberValue = incidentNumberValue.trim();

    /* Single write chokepoint, so this covers every save path — the explicit
       Save button, the notes side panel, and a status change flushing edits. */
    const incidentNumberFormatError = validateIncidentNumber(
      incidentNumberValue,
      incidentNumberFormat,
    );
    if (incidentNumberFormatError) {
      setIncidentNumberError(incidentNumberFormatError);
      toast.error(incidentNumberFormatError);
      setSaveDetailsLoading(false);
      throw new Error(incidentNumberFormatError);
    }

    let notesValue = "";

    if (overrideValues?.notes !== undefined) {
      notesValue = overrideValues.notes;
    } else if (isNotesSidePanelOpen && notesPanelValues?.notes !== undefined) {
      notesValue = notesPanelValues.notes;
    } else if (ownDraft) {
      notesValue = ownDraft.notes;
    } else if (additionalNotesRef?.current?.value) {
      notesValue = additionalNotesRef.current.value;
    }

    let synopsisValue = "";

    if (overrideValues?.synopsis !== undefined) {
      synopsisValue = overrideValues.synopsis;
    } else if (
      isNotesSidePanelOpen &&
      notesPanelValues?.synopsis !== undefined
    ) {
      synopsisValue = notesPanelValues.synopsis;
    } else if (ownDraft) {
      synopsisValue = ownDraft.synopsis;
    } else if (synopsisRef?.current?.value) {
      synopsisValue = synopsisRef.current.value;
    }

    try {
      await patchSubmissionPortal({
        path: { submission_id: idToUse ?? "" },
        body: {
          incident_number: incidentNumberValue,
          notes: notesValue,
          priority: priorityRef?.current?.value
            ? parseInt(priorityRef.current.value)
            : null,
          synopsis: synopsisValue,
          tags: tags.join(","),
        },
      });
      updatedData = {
        ...reportMetadata,
        notes: notesValue,
        incident_number: incidentNumberValue,
        priority: priorityRef?.current?.value
          ? parseInt(priorityRef.current.value)
          : null,
        synopsis: synopsisValue,
        tags: tags.join(","),
      };
      setReportMetadata((prev) => ({
        ...updatedData,
        supplementary_submissions: prev.supplementary_submissions,
      }));
      if (reportMetadata.is_supplemental) {
        applySavedSupplementalNotes(idToUse ?? "", notesValue, synopsisValue);
      }
      await saveSupplementalDrafts();
      toast.success("Details updated successfully");
      setModified(false);
    } catch (error) {
      toast.error("Failed to update details, please try again later");
      throw error;
    } finally {
      setSaveDetailsLoading(false);
    }
    return updatedData;
  }

  async function updateStatus(status: string) {
    try {
      let newMetadata: GetSubmissionResponsePortal = {
        ...reportMetadata,
      };

      // If report is supplementary, check if it has an incident number
      // We dont want to submit report without an incident number
      if (
        isSupplementaryReport &&
        (status === "approved" || status === "escalated")
      ) {
        if (!newMetadata.incident_number) {
          toast.error("Report must have an incident number");
          return;
        }
      }

      if (!isClosed) {
        if (modified || supplementalDraftsRef.current[idToUse ?? ""]) {
          newMetadata = await updateIncidentNumberAndNotes();
        } else {
          await saveSupplementalDrafts();
        }
      }

      newMetadata = {
        ...newMetadata,
        status: status,
        last_updated_by: {
          ...currentUser,
          badge_number: currentUser?.badge_number ?? currentUser?.badge ?? "",
        } as LastUpdatedBy,
      };

      if (status === "approved") {
        newMetadata.approve_note = notifyReportingParty ? specifiedReason : "";
      } else if (status === "rejected") {
        newMetadata.reject_note = specifiedReason;
      } else if (status === "escalated") {
        newMetadata.escalate_note = specifiedReason;
      } else if (status === "submitted" && isReopen) {
        newMetadata = {
          ...newMetadata,
          reopen_note: !notifyReportingParty ? "" : specifiedReason,
          assigned_to: assignReopenToSelf
            ? currentUser
            : newMetadata.assigned_to,
        };
      }

      if (
        serviceConfig?.metadata.automated_incident_number_generation &&
        serviceConfig?.metadata.incident_generation_outcomes?.includes(status)
      ) {
        try {
          const incidentNumberResponse = (
            await postSubmissionGenerateIncidentPortal({
              path: {
                submission_id: idToUse ?? "",
              },
            })
          ).data!;

          newMetadata = {
            ...newMetadata,
            incident_number: incidentNumberResponse.incident_number,
          };
        } catch (error) {
          console.error("Incident number generation failed: ", error);
        }
      }

      if (status === "approved") {
        await patchSubmissionPortal({
          path: {
            submission_id: idToUse ?? "",
          },
          body: !notifyReportingParty
            ? { approve_note: "" }
            : specifiedReason
              ? { approve_note: specifiedReason }
              : {},
        });
      } else if (status === "rejected") {
        await patchSubmissionPortal({
          path: { submission_id: idToUse ?? "" },
          body: specifiedReason ? { reject_note: specifiedReason } : {},
        });
      } else if (status === "modification_requested") {
        await patchSubmissionPortal({
          path: { submission_id: idToUse ?? "" },
          body: specifiedReason
            ? { modification_request_note: specifiedReason }
            : {},
        });
      } else if (status === "escalated") {
        await patchSubmissionPortal({
          path: { submission_id: idToUse ?? "" },
          body: specifiedReason ? { escalate_note: specifiedReason } : {},
        });
      } else if (status === "submitted" && isReopen) {
        await patchSubmissionPortal({
          path: { submission_id: idToUse ?? "" },
          body: {
            was_reopened: isReopen,
            reopen_note: !notifyReportingParty ? "" : specifiedReason,
          },
        });
      }
      await postSubmissionStatusPortal({
        path: { submission_id: idToUse ?? "" },
        body: {
          status: status,
          notify_reporting_party: notifyReportingParty,
        },
      }).then((res) => {
        // newMetadata predates the supplemental notes saved above
        setReportMetadata((prev) => ({
          ...newMetadata,
          supplementary_submissions: prev.supplementary_submissions,
          assigned_to: res.data!.assigned_to,
          status: status,
        }));

        // Update reportsToRender for all reports: full merge for the main report and status for supplementals
        setReportsToRender((prevReports) =>
          prevReports.map((report) =>
            report.submission_id === idToUse
              ? { ...report, ...newMetadata }
              : {
                  ...report,
                  status: newMetadata.status,
                  incident_number: newMetadata.incident_number,
                  last_updated_by: newMetadata.last_updated_by,
                },
          ),
        );
      });

      // Shift to backend once background job system is completed
      const demsExportOutcomes = serviceConfig?.metadata
        ?.dems_export_outcomes ?? ["approved", "escalated"];
      if (
        demsExportOutcomes.includes(status) &&
        serviceConfig?.export_configuration?.dems_export_configuration
      ) {
        if (
          serviceConfig?.export_configuration?.dems_export_configuration
            .provider === "evidence.com"
        ) {
          postSubmissionExportEvidencePortal({
            path: { submission_id: idToUse ?? "" },
          });
        }
      }

      notifyReportingParty
        ? toast.success(
            "Report updated successfully. Email has been sent to the reporting party.",
          )
        : toast.success("Report updated successfully.");

      // Reset the states to default
      setIsReopen(false);
      setNotifyReportingParty(true);
    } catch (error) {
      toast.error("Failed to update status, please try again later");
    }
  }

  const updateTags = (event, newTags: string[]) => {
    setModified(true);
    setTags(newTags);
  };

  const unlinkAllSupplementaryReports = async () => {
    try {
      const responses = reportMetadata.supplementary_submissions.map((report) =>
        unlinkReport(report.submission_id),
      );
      Promise.all(responses).then(() => {
        setUnlinkAllSupplementaryReportsDialogOpen(false);
        convertToSupplementaryReport().then(() => {
          setConvertGeneralToSupplementaryDialogOpen(false);
        });
      });
    } catch (error) {
      console.error("Error while unlinking supplementary reports:", error);
      toast.error("Failed to unlink supplementary reports");
    }
  };

  if (!(reportMetadata && reportMetadata.status)) {
    return (
      <div style={{ margin: "60px auto" }}>
        <CircularProgress />
      </div>
    );
  }

  const shareReportButton = () => {
    return (
      <Tooltip title="Share Report" arrow>
        <IconButton
          onClick={() => {
            setNotesSidebarTab("sharing");
            openNotesSidePanel();
          }}
          aria-label="Share Report"
          size="small"
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            color: "text.primary",
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: alpha(colors.common.black, 0.04),
            },
            "& .MuiSvgIcon-root": {
              width: 20,
              height: 20,
            },
          }}
        >
          <Share />
        </IconButton>
      </Tooltip>
    );
  };

  const reportToBulletinButton = (disabled: boolean = true) => {
    return (
      <Tooltip
        title={
          disabled
            ? "Create bulletin in RubiALERT. Takes information and evidence files from online report and creates a draft bulletin for you to review. (Permission required)"
            : "Create bulletin in RubiALERT. Takes information and evidence files from online report and creates a draft bulletin for you to review."
        }
        arrow
      >
        <span>
          <Button
            onClick={() => {
              if (!disabled) {
                // Open the tab synchronously, within the click handler, so
                // Chrome's popup blocker sees it as user-initiated. We
                // navigate it to the real URL once the API call resolves.
                const newTab = window.open("", "_blank");
                setIsCreatingBulletin(true);
                postSubmissionCreateBulletinUrl({
                  path: { submission_id: idToUse ?? "" },
                })
                  .then((res) => {
                    const response = res.data!;
                    // The form has the row created for it and opens on the edit route
                    // (decision 53).
                    const bulletinUrl = bulletinFromReportPath(response.token);
                    if (newTab) {
                      newTab.location.href = bulletinUrl;
                    } else {
                      // The synchronous open was still blocked (e.g. strict
                      // browser settings) - surface a manual link instead of
                      // failing silently.
                      toast(
                        <span>
                          Bulletin draft created, but the popup was blocked.{" "}
                          <Link
                            href={bulletinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Click here to open it
                          </Link>
                        </span>,
                        { autoClose: false },
                      );
                    }
                  })
                  .catch(() => {
                    newTab?.close();
                    toast.error("Failed to create bulletin. Please try again.");
                  })
                  .finally(() => {
                    setIsCreatingBulletin(false);
                  });
              }
            }}
            disabled={disabled || isCreatingBulletin}
            aria-label="Create Bulletin"
            variant="outlined"
            startIcon={
              isCreatingBulletin ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <PostAdd />
              )
            }
            sx={{
              backgroundColor: "transparent",
              color: disabled ? "#ccc" : "primary.main",
              border: "2px solid",
              borderColor: disabled ? "#ccc" : "primary.main",
              borderRadius: { xs: "50%", sm: "6px" },
              padding: { xs: "4px 8px", sm: "6px 12px", md: "8px 16px" },
              fontSize: { xs: "10px", sm: "11px", md: "12px" },
              fontWeight: "600",
              textTransform: "uppercase",
              "&:hover": {
                backgroundColor: disabled ? "transparent" : "primary.main",
                color: disabled ? "#ccc" : "primary.contrastText",
                borderColor: disabled ? "#ccc" : "primary.main",
              },
              "&.Mui-disabled": {
                backgroundColor: "transparent",
                color: "#999",
                border: "2px solid #ccc",
              },
            }}
          >
            {isCreatingBulletin ? "Creating..." : "Bulletin"}
          </Button>
        </span>
      </Tooltip>
    );
  };

  const downloadPDFButton = () => {
    return (
      <Tooltip title="Download PDF" arrow>
        <IconButton
          onClick={downloadPDF}
          aria-label="Download PDF"
          size="small"
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            color: "text.primary",
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: alpha(colors.common.black, 0.04),
            },
            "& .MuiSvgIcon-root": {
              width: 20,
              height: 20,
            },
          }}
        >
          <FileDownload />
        </IconButton>
      </Tooltip>
    );
  };

  const downloadSubmissionJson = () => {
    if (!reportMetadata?.submission_id) return;
    const json = JSON.stringify(reportMetadata, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `submission-${reportMetadata.submission_id}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadSubmissionJsonButton = () => {
    if (!cisoDisplay) return null;
    return (
      <Tooltip title="Download submission JSON" arrow>
        <IconButton
          onClick={downloadSubmissionJson}
          aria-label="Download submission JSON"
          size="small"
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            color: "text.primary",
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: alpha(colors.common.black, 0.04),
            },
            "& .MuiSvgIcon-root": {
              width: 20,
              height: 20,
            },
          }}
        >
          <DataObject />
        </IconButton>
      </Tooltip>
    );
  };

  const editNotesButton = () => {
    return (
      <Tooltip
        title={isNotesSidePanelOpen ? "Hide notes panel" : "Show notes panel"}
        arrow
      >
        <IconButton
          onClick={() => toggleNotesSidePanel()}
          aria-label="Toggle notes panel"
          size="small"
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            color: isNotesSidePanelOpen ? colors.primary.main : "text.primary",
            backgroundColor: isNotesSidePanelOpen
              ? "rgba(25,118,210,0.12)"
              : "transparent",
            "&:hover": {
              backgroundColor: isNotesSidePanelOpen
                ? "rgba(25,118,210,0.18)"
                : alpha(colors.common.black, 0.04),
            },
            "& svg": {
              width: 20,
              height: 20,
            },
          }}
        >
          {/* Single right-drawer icon, matching the design's panel-toggle glyph */}
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2zm12 14V5h4v14h-4z" />
          </svg>
        </IconButton>
      </Tooltip>
    );
  };

  const convertToSupplementaryReportButton = () => {
    return (
      <Tooltip
        title={isExported ? "Exported records cannot be modified" : ""}
        arrow
      >
        <span>
          <Button
            variant="contained"
            color="info"
            size="medium"
            disabled={isExported}
            onClick={() => {
              if (reportMetadata.supplementary_submissions.length > 0) {
                setUnlinkAllSupplementaryReportsDialogOpen(true);
              } else {
                setConvertGeneralToSupplementaryDialogOpen(true);
              }
            }}
            endIcon={<Transform />}
            sx={{
              fontSize: { xs: "9px", sm: "10px", md: "11px", lg: "12px" },
              padding: {
                xs: "4px 8px",
                sm: "6px 10px",
                md: "6px 12px",
                lg: "8px 16px",
              },
              minWidth: { xs: "auto", sm: "auto" },
              whiteSpace: "nowrap",
              textTransform: "uppercase",
              fontWeight: "600",
              borderRadius: { xs: "50%", sm: "6px" },
              width: { xs: "40px", sm: "auto" },
              height: { xs: "40px", sm: "auto" },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "& .MuiButton-endIcon": {
                marginLeft: { xs: "0", sm: "6px", md: "8px" },
                marginRight: "0",
              },
            }}
          >
            <Box
              sx={{
                display: { xs: "none", sm: "inline" },
              }}
            >
              Convert to Supplementary Report
            </Box>
          </Button>
        </span>
      </Tooltip>
    );
  };

  const convertToGeneralReportButton = () => {
    return [
      <Tooltip
        title={isExported ? "Exported records cannot be modified" : ""}
        arrow
        key={"convert"}
      >
        <span>
          <Button
            variant="contained"
            color="info"
            size="medium"
            disabled={isExported}
            onClick={() => {
              setIsNoLinkFoundDialogOpen(true);
            }}
            endIcon={<Transform />}
            sx={{
              fontSize: { xs: "9px", sm: "10px", md: "11px", lg: "12px" },
              padding: {
                xs: "4px 8px",
                sm: "6px 10px",
                md: "6px 12px",
                lg: "8px 16px",
              },
              minWidth: { xs: "auto", sm: "auto" },
              whiteSpace: "nowrap",
              textTransform: "uppercase",
              fontWeight: "600",
              borderRadius: { xs: "50%", sm: "6px" },
              width: { xs: "40px", sm: "auto" },
              height: { xs: "40px", sm: "auto" },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "& .MuiButton-endIcon": {
                marginLeft: { xs: "0", sm: "6px", md: "8px" },
                marginRight: "0",
              },
            }}
          >
            <Box
              sx={{
                display: { xs: "none", sm: "inline" },
              }}
            >
              Convert to General Report
            </Box>
          </Button>
        </span>
      </Tooltip>,
    ];
  };

  const handleDeleteSubmissionConfirm = async () => {
    if (!idToUse) return;
    setIsDeletingSubmission(true);
    try {
      await deleteIntelSubmission({ path: { id: idToUse } });
      toast.success("Submission deleted");
      setDeleteSubmissionDialogOpen(false);
      if (onSubmissionDeleted) {
        onSubmissionDeleted();
      } else {
        // The report is gone; there is nothing left to save.
        leaveGuard.allowNextNavigation();
        navigate(`/rubiintel/${orgId}/reports`);
      }
    } catch (error) {
      console.error("Error deleting intel submission:", error);
      toast.error("Failed to delete submission");
    } finally {
      setIsDeletingSubmission(false);
    }
  };

  const deleteSubmissionButton = () => {
    return (
      <Button
        key="delete-submission"
        variant="contained"
        color="error"
        size="medium"
        onClick={() => setDeleteSubmissionDialogOpen(true)}
        endIcon={<Delete />}
        sx={{
          fontSize: { xs: "9px", sm: "10px", md: "11px", lg: "12px" },
          padding: {
            xs: "4px 8px",
            sm: "6px 10px",
            md: "6px 12px",
            lg: "8px 16px",
          },
          minWidth: { xs: "auto", sm: "auto" },
          whiteSpace: "nowrap",
          textTransform: "uppercase",
          fontWeight: "600",
          borderRadius: { xs: "50%", sm: "6px" },
          width: { xs: "40px", sm: "auto" },
          height: { xs: "40px", sm: "auto" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& .MuiButton-endIcon": {
            marginLeft: { xs: "0", sm: "6px", md: "8px" },
            marginRight: "0",
          },
        }}
      >
        <Box sx={{ display: { xs: "none", sm: "inline" } }}>
          Delete Submission
        </Box>
      </Button>
    );
  };

  const closeReportButton = () => {
    return (
      <Button
        variant="contained"
        color="error"
        size="medium"
        onClick={() => actionSelected("closeSupplementary")}
        endIcon={<Lock />}
        sx={{
          fontSize: { xs: "9px", sm: "10px", md: "11px", lg: "12px" },
          padding: {
            xs: "4px 8px",
            sm: "6px 10px",
            md: "6px 12px",
            lg: "8px 16px",
          },
          minWidth: { xs: "auto", sm: "auto" },
          whiteSpace: "nowrap",
          textTransform: "uppercase",
          fontWeight: "600",
          borderRadius: { xs: "50%", sm: "6px" },
          width: { xs: "40px", sm: "auto" },
          height: { xs: "40px", sm: "auto" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& .MuiButton-endIcon": {
            marginLeft: { xs: "0", sm: "6px", md: "8px" },
            marginRight: "0",
          },
        }}
      >
        <Box
          sx={{
            display: { xs: "none", sm: "inline" },
          }}
        >
          Close Report
        </Box>
      </Button>
    );
  };

  const reportListButton = () => {
    return (
      <LinkSupplementaryDropdown
        selectedReportToLink={selectedReportToLink}
        setSelectedReportToLink={setSelectedReportToLink}
        setIsLinkingDialogOpen={setIsLinkingDialogOpen}
      />
    );
  };

  const modifyButton = () => {
    return (
      <Tooltip title={modalDetails.modification_requested.description}>
        <Button
          variant="contained"
          color={reportColourTypes["modification_requested"]}
          size="medium"
          onClick={() => actionSelected("modification_requested")}
          endIcon={<Summarize />}
          sx={{
            fontSize: { xs: "9px", sm: "10px", md: "12px" },
            padding: { xs: "4px 8px", sm: "6px 12px", md: "8px 16px" },
            minWidth: { xs: "auto", sm: "auto" },
            whiteSpace: "nowrap",
            textTransform: "uppercase",
            fontWeight: "600",
            borderRadius: { xs: "50%", sm: "6px" },
            width: { xs: "40px", sm: "auto" },
            height: { xs: "40px", sm: "auto" },
            "& .MuiButton-endIcon": {
              marginLeft: { xs: "0", sm: "6px", md: "8px" },
            },
          }}
        >
          <Box
            sx={{
              display: { xs: "none", sm: "inline" },
            }}
          >
            Follow-up
          </Box>
        </Button>
      </Tooltip>
    );
  };

  const approveButton = () => {
    return (
      <Tooltip title={modalDetails.approved.description}>
        <Button
          variant="contained"
          color={reportColourTypes["approved"]}
          size="medium"
          onClick={() => actionSelected("approved")}
          endIcon={<CheckCircleOutline />}
          sx={{
            fontSize: { xs: "9px", sm: "10px", md: "12px" },
            padding: { xs: "4px 8px", sm: "6px 12px", md: "8px 16px" },
            minWidth: { xs: "auto", sm: "auto" },
            whiteSpace: "nowrap",
            textTransform: "uppercase",
            fontWeight: "600",
            borderRadius: { xs: "50%", sm: "6px" },
            width: { xs: "40px", sm: "auto" },
            height: { xs: "40px", sm: "auto" },
            "& .MuiButton-endIcon": {
              marginLeft: { xs: "0", sm: "6px", md: "8px" },
            },
          }}
        >
          <Box
            sx={{
              display: { xs: "none", sm: "inline" },
            }}
          >
            Approve
          </Box>
        </Button>
      </Tooltip>
    );
  };

  const rejectButton = () => {
    return (
      <Tooltip title={modalDetails.rejected.description}>
        <Button
          variant="contained"
          color={reportColourTypes["rejected"]}
          size="medium"
          onClick={() => actionSelected("rejected")}
          endIcon={<Block />}
          sx={{
            fontSize: { xs: "9px", sm: "10px", md: "12px" },
            padding: { xs: "4px 8px", sm: "6px 12px", md: "8px 16px" },
            minWidth: { xs: "auto", sm: "auto" },
            whiteSpace: "nowrap",
            textTransform: "uppercase",
            fontWeight: "600",
            borderRadius: { xs: "50%", sm: "6px" },
            width: { xs: "40px", sm: "auto" },
            height: { xs: "40px", sm: "auto" },
            "& .MuiButton-endIcon": {
              marginLeft: { xs: "0", sm: "6px", md: "8px" },
            },
          }}
        >
          <Box
            sx={{
              display: { xs: "none", sm: "inline" },
            }}
          >
            Reject
          </Box>
        </Button>
      </Tooltip>
    );
  };

  const escalateButton = () => {
    return (
      <Tooltip title={modalDetails.escalated.description}>
        <Button
          variant="contained"
          color={reportColourTypes["escalated"]}
          size="medium"
          onClick={() => actionSelected("escalated")}
          endIcon={<ArrowUpward />}
          sx={{
            fontSize: { xs: "9px", sm: "10px", md: "12px" },
            padding: { xs: "4px 8px", sm: "6px 12px", md: "8px 16px" },
            minWidth: { xs: "auto", sm: "auto" },
            whiteSpace: "nowrap",
            textTransform: "uppercase",
            fontWeight: "600",
            borderRadius: { xs: "50%", sm: "6px" },
            width: { xs: "40px", sm: "auto" },
            height: { xs: "40px", sm: "auto" },
            "& .MuiButton-endIcon": {
              marginLeft: { xs: "0", sm: "6px", md: "8px" },
            },
          }}
        >
          <Box
            sx={{
              display: { xs: "none", sm: "inline" },
            }}
          >
            Escalate
          </Box>
        </Button>
      </Tooltip>
    );
  };

  const handleReExportConfirm = async () => {
    if (!idToUse) return;
    setReExportLoading(true);
    try {
      const promises: Promise<unknown>[] = [];

      if (reExportOption === "rms" || reExportOption === "both") {
        promises.push(
          postSubmissionClearExportPortal({
            path: { submission_id: idToUse },
          }),
        );
      }

      if (reExportOption === "dems" || reExportOption === "both") {
        promises.push(
          postSubmissionExportEvidencePortal({
            path: { submission_id: idToUse },
          }),
        );
      }

      await Promise.all(promises);

      if (reExportOption === "rms" || reExportOption === "both") {
        const now = new Date().toISOString();
        setReportMetadata((prev) => ({
          ...prev,
          last_export_time: null,
          time_updated: now,
        }));
      }

      setIsReExportModalOpen(false);

      const successKey =
        reExportOption === "both"
          ? "re_export_success_both"
          : reExportOption === "dems"
            ? "re_export_success_dems"
            : "re_export_success_rms";
      toast.success(t(successKey));
    } catch {
      toast.error(t("re_export_error"));
    } finally {
      setReExportLoading(false);
    }
  };

  const reopenButtonOrExportLabel = () => {
    const lastExportTime = reportMetadata?.last_export_time;
    if (lastExportTime) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: "11px", sm: "12px" },
              color: "text.secondary",
              whiteSpace: "nowrap",
            }}
          >
            {t("last_exported_at", {
              date: formatDateShort(lastExportTime),
            })}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const hasRms =
                !!serviceConfig?.export_configuration?.rms_export_configuration;
              const hasDems =
                !!serviceConfig?.export_configuration
                  ?.dems_export_configuration;
              if (hasRms && hasDems) {
                setReExportOption("both");
              } else if (hasDems) {
                setReExportOption("dems");
              } else {
                setReExportOption("rms");
              }
              setIsReExportModalOpen(true);
            }}
            sx={{
              fontSize: { xs: "10px", sm: "11px" },
              padding: { xs: "4px 8px", sm: "6px 12px" },
              textTransform: "uppercase",
              fontWeight: "600",
            }}
          >
            {t("re_export_button")}
          </Button>
        </Box>
      );
    }
    return (
      <Tooltip title={modalDetails.submitted.description}>
        <Button
          variant="contained"
          color={reportColourTypes["submitted"]}
          size="medium"
          onClick={() => {
            setIsReopen(true);
            actionSelected("submitted");
          }}
          endIcon={<LockOpen />}
          sx={{
            fontSize: { xs: "9px", sm: "10px", md: "12px" },
            padding: { xs: "4px 8px", sm: "6px 12px", md: "8px 16px" },
            minWidth: { xs: "auto", sm: "auto" },
            whiteSpace: "nowrap",
            textTransform: "uppercase",
            fontWeight: "600",
            borderRadius: { xs: "50%", sm: "6px" },
            width: { xs: "40px", sm: "auto" },
            height: { xs: "40px", sm: "auto" },
            "& .MuiButton-endIcon": {
              marginLeft: { xs: "0", sm: "6px", md: "8px" },
            },
          }}
        >
          <Box
            sx={{
              display: { xs: "none", sm: "inline" },
            }}
          >
            {t("reopen_report_title")}
          </Box>
        </Button>
      </Tooltip>
    );
  };

  const infoButton = () => {
    return (
      <IconButton
        onClick={() => setIsBtnInfoDialogOpen(true)}
        size="small"
        style={{ marginLeft: "8px" }}
      >
        <Info />
      </IconButton>
    );
  };

  const getButtonGroups = (): JSX.Element[][] => {
    const setLeftButtons = () => {
      const defaultButtons: JSX.Element[] = [];
      if (!isClosed && canUpdateReports) {
        if (isSupplementaryReport) {
          defaultButtons.push(...convertToGeneralReportButton());
          defaultButtons.push(closeReportButton());
          return defaultButtons;
        }

        if (!cisoDisplay) {
          defaultButtons.push(convertToSupplementaryReportButton());
        }
      }
      return defaultButtons;
    };
    const setActionButtons = () => {
      const intelDeleteActions: JSX.Element[] =
        cisoDisplay && canDeleteEntriesIntel ? [deleteSubmissionButton()] : [];

      if (!canUpdateReports) {
        return intelDeleteActions;
      }
      if (!isClosed) {
        if (isSupplementaryReport) {
          return [reportListButton(), ...intelDeleteActions];
        }

        if (cisoDisplay) {
          return [modifyButton(), approveButton(), ...intelDeleteActions];
        }
        const defaultActions = [
          approveButton(),
          rejectButton(),
          escalateButton(),
          infoButton(),
        ];
        if (!organizationConfig?.disableModificationRequest) {
          defaultActions.unshift(modifyButton());
        }
        return defaultActions;
      }
      return [reopenButtonOrExportLabel(), ...intelDeleteActions];
    };
    return [setLeftButtons(), setActionButtons()];
  };

  const surveyTitle =
    extractLocaleStringFromField(
      surveyResult.survey?.configuration.title as string | ILocaleObject,
    ) ?? "";

  if (!canViewIndividualReport && !cisoDisplay) {
    return (
      <DisabledComponentDialog
        isDialogOpen={true}
        handleClose={() => {
          navigate(`/`);
        }}
        title="You do not have access to view this report."
        message={
          <Typography>
            Please contact{" "}
            <Link
              rel="noopener noreferrer"
              target="_blank"
              href={mailTo({
                email: "support@tryrubicon.com",
                subject: "Requesting Access to View Reports",
                body: `Hello Rubicon Support Team,

                I would like to request access to view reports.

                Thank you.

                Sincerely,

                ${currentUser?.first_name} ${currentUser?.last_name}
                ${currentUser?.email}`,
              })}
              color="secondary"
            >
              support@tryrubicon.com
            </Link>{" "}
            to request access.
          </Typography>
        }
      />
    );
  }

  const content = (
    <div className={styles.wrapperContainer}>
      {/* Image Match Banner */}
      <ImageMatchBanner />

      {/* Top Bar */}
      <PageHeader
        measureHeight
        left={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: "6px", sm: "8px", md: "12px" },
              flex: "1 0 0",
            }}
          >
            <Tooltip title="Back" arrow>
              <IconButton
                onClick={goBack}
                aria-label="Back"
                sx={{
                  backgroundColor: "action.hover",
                  color: "text.secondary",
                  width: { xs: "28px", sm: "30px", md: "32px" },
                  height: { xs: "28px", sm: "30px", md: "32px" },
                  borderRadius: "50%",
                  "&:hover": {
                    backgroundColor: "divider",
                  },
                }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                reportStatusTooltips[reportMetadata.status as ReportStatus] ||
                ""
              }
              arrow
            >
              <Chip
                label={
                  statusTypes[reportMetadata.status as ReportStatus] ??
                  reportMetadata.status
                }
                color={
                  reportColourTypes[reportMetadata.status as ReportStatus] ??
                  "default"
                }
                size="medium"
                sx={{
                  borderRadius: "100px",
                  fontWeight: 400,
                  textTransform: "uppercase",
                  height: 32,
                  fontSize: 13,
                  letterSpacing: "0.16px",
                  lineHeight: "18px",
                  px: 1.5,
                  py: 0.5,
                  display: "inline-flex",
                  alignItems: "center",
                }}
              />
            </Tooltip>
            {/* Viewer avatars */}
            {viewers.length > 0 &&
              (() => {
                const currentUserId =
                  currentUser?.id != null ? String(currentUser.id) : null;
                if (viewers.length === 0) return null;
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mr: 1,
                      "& > .MuiTooltip-root": {},
                    }}
                  >
                    {viewers.map((v, idx) => {
                      const isYou =
                        currentUserId != null && v.user_id === currentUserId;
                      const editing = v.is_editing;
                      const label = `${v.first_name} ${v.last_name}${isYou ? " (you)" : ""}${editing ? " — editing" : ""}`;
                      return (
                        <Tooltip key={v.user_id} title={label}>
                          <Box
                            sx={{
                              position: "relative",
                              display: "inline-flex",
                              overflow: "visible",
                              ml: idx === 0 ? 0 : "-8px",
                            }}
                          >
                            <Badge
                              variant="dot"
                              color="success"
                              invisible={!editing}
                              sx={{
                                "& .MuiBadge-badge": {
                                  right: 0,
                                  top: 0,
                                  minWidth: 10,
                                  height: 10,
                                  border: "2px solid",
                                  borderColor: "background.paper",
                                },
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  fontSize: 12,
                                  fontWeight: 500,
                                  bgcolor: isYou
                                    ? "secondary.main"
                                    : "primary.main",
                                  color: colors.common.white,
                                  border: isYou
                                    ? "2px solid var(--primary-main)"
                                    : "2px solid white",
                                  cursor: "pointer",
                                  transition: "transform 0.15s",
                                  "&:hover": {
                                    transform: "translateY(-3px)",
                                    zIndex: 10,
                                  },
                                  ...(editing && {
                                    boxShadow: (t) =>
                                      `0 0 0 2px ${t.palette.success.main}`,
                                  }),
                                }}
                              >
                                {[v.first_name, v.last_name]
                                  .map((s) => (s ?? "").charAt(0))
                                  .join("")
                                  .toUpperCase() || "?"}
                              </Avatar>
                            </Badge>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                );
              })()}
            <AssignedToSelector
              currentAssignee={reportMetadata.assigned_to ?? null}
              currentUser={currentUser}
              onAssign={(user_id: string | null) => {
                updateAssignee(user_id);
              }}
              userOptions={assignableUsers}
              disableEdit={isClosed || !canUpdateReports}
            />
          </Box>
        }
        middle={
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: "1px", sm: "2px" },
              flex: 1,
              alignItems: "center",
              minWidth: 0, // Allow text truncation
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "text.primary",
                textAlign: "center",
                fontSize: { xs: "15px", sm: "16px", md: "17px" },
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
            >
              {getReportTitle(isSupplementaryReport, surveyTitle)}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: "4px", sm: "6px" },
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "11px", sm: "12px", md: "13px" },
                  lineHeight: 1,
                  display: { xs: "none", sm: "block" },
                }}
              >
                {reportMetadata.time_submitted &&
                  reportMetadata.time_submitted.length > 0 &&
                  formatDateShort(reportMetadata.time_submitted)}
              </Typography>
              <Chip
                label={reportMetadata.tracking_number}
                size="small"
                sx={{
                  backgroundColor: "action.hover",
                  color: "text.primary",
                  fontSize: { xs: "14px", sm: "15px", md: "16px" },
                  height: { xs: "18px", sm: "20px" },
                  borderRadius: "4px",
                }}
              />
              <IconButton
                size="small"
                onClick={() => {
                  const trackingNumber =
                    reportMetadata.tracking_number ||
                    "No tracking number available";
                  navigator.clipboard.writeText(trackingNumber);
                }}
                title="Copy tracking number"
                sx={{
                  padding: "1px",
                  width: { xs: "18px", sm: "20px" },
                  height: { xs: "18px", sm: "20px" },
                }}
              >
                <ContentCopy
                  fontSize="small"
                  sx={{ color: "text.secondary" }}
                />
              </IconButton>
              {reportMetadata.incident_number && (
                <>
                  <Chip
                    label={reportMetadata.incident_number}
                    size="small"
                    sx={{
                      backgroundColor: "action.hover",
                      color: "text.primary",
                      fontSize: { xs: "14px", sm: "15px", md: "16px" },
                      height: { xs: "18px", sm: "20px" },
                      borderRadius: "4px",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      const incidentNumber =
                        reportMetadata.incident_number ||
                        "No incident number available";
                      navigator.clipboard.writeText(incidentNumber);
                    }}
                    title="Copy incident number"
                    sx={{
                      padding: "1px",
                      width: { xs: "18px", sm: "20px" },
                      height: { xs: "18px", sm: "20px" },
                    }}
                  >
                    <ContentCopy
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
        }
        right={
          <Box
            sx={{
              display: "flex",
              gap: { xs: "4px", sm: "6px" },
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              flex: "1 0 0",
            }}
          >
            {!cisoDisplay &&
              reportToBulletinButton(!userHasBulletinCreatePermission)}
            {canUpdateReports && editNotesButton()}
            {shareReportButton()}
            {downloadPDFButton()}
            {downloadSubmissionJsonButton()}
          </Box>
        }
      />

      {/* Content Area - this gets pushed when drawer is open */}
      <div
        className={`${styles.contentArea}${
          isNotesSidePanelOpen && !isMobile && !isPanelTakingOver
            ? ` ${styles.contentWithDrawer}`
            : ""
        }`}
        style={
          isNotesSidePanelOpen && !isMobile && !isPanelTakingOver
            ? ({
                "--drawer-width": `${notesPanelWidth + 32}px`,
              } as React.CSSProperties)
            : {}
        }
      >
        {/* Record Information - Hidden when notes drawer is open */}
        {!isNotesSidePanelOpen && (
          <div>
            <Paper
              elevation={3}
              className={styles.paperOfficerDetailsContainer}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "16px",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <h3>{!cisoDisplay && <>Police </>}Record Information</h3>
              </div>

              {!cisoDisplay && (
                <Tooltip
                  title={
                    isExported ? "Exported records cannot be modified" : ""
                  }
                  arrow
                >
                  <FormControl fullWidth>
                    <TextField
                      id="incidentNumber"
                      className={styles.incidentNumber}
                      key={reportMetadata?.incident_number}
                      inputRef={incidentNumberRef}
                      disabled={isClosed || isExported}
                      variant="outlined"
                      label="RMS Incident / Occurrence Number"
                      error={Boolean(incidentNumberError)}
                      helperText={incidentNumberHelperText()}
                      placeholder={
                        serviceConfig?.metadata
                          .automated_incident_number_generation
                          ? `Auto-generated when the record is ${serviceConfig.metadata.incident_generation_outcomes?.join(", ")}`
                          : "Enter Incident Number"
                      }
                      onChange={(e) => {
                        setIncidentNumberError(
                          validateIncidentNumber(
                            e.target.value,
                            incidentNumberFormat,
                          ),
                        );
                        if (!modified) {
                          setModified(true);
                        }
                      }}
                    />
                  </FormControl>
                </Tooltip>
              )}
              <Tooltip
                title={isExported ? "Exported records cannot be modified" : ""}
                arrow
              >
                <FormControl fullWidth>
                  <TextField
                    id="additionalNotes"
                    inputRef={additionalNotesRef}
                    className={styles.additionalNotes}
                    disabled={isClosed || isExported}
                    variant="outlined"
                    label="Internal Private Notes"
                    multiline
                    minRows={4}
                    onChange={() => {
                      if (!modified) {
                        setModified(true);
                      }
                    }}
                  />
                </FormControl>
              </Tooltip>

              {!cisoDisplay && priorityOptions.length > 0 && (
                <Tooltip
                  title={
                    isExported ? "Exported records cannot be modified" : ""
                  }
                  arrow
                >
                  <FormControl fullWidth>
                    <TextField
                      disabled={isClosed || isExported}
                      variant="outlined"
                      label="Priority"
                      inputRef={priorityRef}
                      onChange={() => {
                        if (!modified) {
                          setModified(true);
                        }
                      }}
                      defaultValue={reportMetadata?.priority?.toString() ?? "0"}
                      select
                    >
                      {priorityOptions.map((option) => (
                        <MenuItem
                          key={option.name}
                          value={option.value.toString()}
                        >
                          {option.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>
                </Tooltip>
              )}
              <div>
                <Typography variant="h6">
                  Synopsis
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    size="medium"
                    sx={{ margin: 2 }}
                    onClick={() => generateSynopsis()}
                    loading={isGeneratingSynopsis}
                    disabled={isClosed || isExported}
                    hidden={isClosed || isExported}
                  >
                    {synopsisRef.current?.value ? (
                      <>Regenerate</>
                    ) : (
                      <>
                        Generate <AutoFixHighRounded />
                      </>
                    )}
                  </LoadingButton>
                </Typography>
                {!isClosed && !isExported && (
                  <Typography variant="body2">
                    <b>
                      For the best synopsis, please correct any errors and add
                      missing details to the report first. The synopsis will be
                      included in the RMS export but won&apos;t be shared with
                      the reporting individual.
                      {requireSynopsisVerification &&
                        " You must verify the accuracy of the information before proceeding."}
                    </b>
                  </Typography>
                )}
                {isGeneratingSynopsis ? (
                  <div
                    style={{
                      border: "1px solid lightgrey",
                      borderRadius: "4px",
                      padding: "8px",
                      marginTop: "16px",
                    }}
                  >
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" />
                  </div>
                ) : (
                  <Tooltip
                    title={
                      isExported ? "Exported records cannot be modified" : ""
                    }
                    arrow
                  >
                    <FormControl fullWidth>
                      <TextField
                        id="synopsis"
                        className={styles.synopsis}
                        disabled={isClosed || isExported}
                        variant="outlined"
                        inputRef={synopsisRef}
                        multiline
                        placeholder="No synopsis generated yet."
                        helperText={
                          synopsisRef.current?.value
                            ? "Generated synopsis may contain errors. Please review and update important information."
                            : ""
                        }
                        minRows={4}
                        onChange={(e) => {
                          if (!modified) {
                            setModified(true);
                          }
                          setSynopsisVerified(false);
                          setHasSynopsisContent(
                            !!(e.target.value?.trim?.() ?? ""),
                          );
                        }}
                      />
                    </FormControl>
                  </Tooltip>
                )}
                {!isClosed && !isExported && enforceSynopsisVerification && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={synopsisVerified}
                        disabled={synopsisVerifiedLoading}
                        onChange={async (e) => {
                          if (!e.target.checked) {
                            setSynopsisVerified(false);
                            return;
                          }
                          setSynopsisVerifiedLoading(true);
                          try {
                            await postSubmissionSynopsisVerifiedPortal({
                              path: { submission_id: idToUse ?? "" },
                            });
                            setSynopsisVerified(true);
                          } catch {
                            toast.error(
                              "Failed to record synopsis verification. Please try again.",
                            );
                            setSynopsisVerified(false);
                          } finally {
                            setSynopsisVerifiedLoading(false);
                          }
                        }}
                      />
                    }
                    label="I confirm that I have verified the details of this synopsis"
                    sx={{ mt: 1.5 }}
                  />
                )}
              </div>
              {displayTagsField && (
                <Tooltip
                  title={
                    isExported ? "Exported records cannot be modified" : ""
                  }
                  arrow
                >
                  <FormControl fullWidth>
                    <Autocomplete
                      fullWidth
                      multiple
                      disabled={isClosed || isExported}
                      options={tagOptions}
                      id="tags-autocomplete"
                      value={tags}
                      onChange={updateTags} //handleEmailChange
                      filterSelectedOptions
                      freeSolo
                      loading={false}
                      filterOptions={(_options, params) => {
                        const filtered: string[] = [...tagOptions];
                        if (params.inputValue !== "") {
                          filtered.push(params.inputValue);
                        }

                        return filtered;
                      }}
                      getOptionLabel={(option) => option}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Tags"
                          placeholder="Add tags..."
                        />
                      )}
                    />
                  </FormControl>
                </Tooltip>
              )}
              {!isClosed && !isExported && (
                <LoadingButton
                  variant="contained"
                  color="info"
                  size="medium"
                  fullWidth
                  sx={{ marginBottom: 2 }}
                  onClick={async () => {
                    const proceed = await guardIncidentNumber(
                      incidentNumberRef.current?.value ?? "",
                    );
                    if (!proceed) return;
                    await updateIncidentNumberAndNotes();
                  }}
                  loading={saveDetailsLoading}
                  disabled={!hasUnsavedChanges || Boolean(incidentNumberError)}
                >
                  Save Changes
                </LoadingButton>
              )}
            </Paper>
          </div>
        )}
        <EmailNotesCard
          isClosed={isClosed}
          status={reportMetadata.status}
          notes={notes}
          lastUpdatedBy={reportMetadata.last_updated_by}
          timeUpdated={reportMetadata.time_updated}
        />
        <CrimeCodesCard
          orgId={orgId ?? ""}
          submissionId={idToUse ?? ""}
          surveyClass={reportMetadata?.survey_class ?? ""}
          enabled={!!serviceConfig?.metadata.enable_crime_codes}
          readOnly={isClosed}
          required={Boolean(currentSurveyConfig?.mandatory_crime_codes)}
          onCodesChange={setCrimeCodeCount}
        />

        {/* Condensed Report */}
        <div style={{ position: "relative" }}>
          {reportsToRender.map((report) => (
            <RenderCondensedReport
              key={report.submission_id}
              reportMetadata={report}
              setSelectedIndex={setSelectedIndex}
              selectedIndex={selectedIndex}
              setIsSlideShowActive={setIsSlideShowActive}
              isSlideShowActive={isSlideShowActive}
              parentReportStatus={reportMetadata?.status}
              onReportUpdated={getReportData}
              onEditStateChange={setEditState}
              onBeforeEdit={confirmNoOtherEditors}
              isNotesSidePanelOpen={isNotesSidePanelOpen}
              supplementalDraft={supplementalDrafts[report.submission_id]}
              onSupplementalDraftChange={(draft) =>
                setSupplementalDraft(report.submission_id, draft)
              }
              onSupplementalNotesSaved={applySavedSupplementalNotes}
              canUpdateReports={canUpdateReports}
              // Mirror the gates and unlink-all branch of
              // convertToSupplementaryReportButton — without these the menu
              // item silently bypasses isExported / isClosed / cisoDisplay /
              // canUpdateReports and skips the unlink-all flow when the
              // report has linked supplementaries.
              onConvertToSupplementary={
                !isExported &&
                !isClosed &&
                !cisoDisplay &&
                canUpdateReports &&
                !report.is_supplemental
                  ? () => {
                      if (
                        reportMetadata.supplementary_submissions &&
                        reportMetadata.supplementary_submissions.length > 0
                      ) {
                        setUnlinkAllSupplementaryReportsDialogOpen(true);
                      } else {
                        setConvertGeneralToSupplementaryDialogOpen(true);
                      }
                    }
                  : undefined
              }
              // surveyResult={surveyResult}
            />
          ))}
        </div>

        {/* Reviewer Details Section — optional sign-off form some surveys
            configure via reviewer_details_survey. */}
        {reviewerDetailsSurveyConfig && (
          <ReviewerDetails model={reviewerDetailsSurveyModel} />
        )}
      </div>

      <BottomFixedActionContainer buttonGroups={getButtonGroups()} />
      <DeleteSubmissionDialog
        isOpen={deleteSubmissionDialogOpen}
        isDeleting={isDeletingSubmission}
        onClose={() => setDeleteSubmissionDialogOpen(false)}
        onConfirm={handleDeleteSubmissionConfirm}
      />
      {/* Action Modal */}
      <ActionModal
        isModalOpen={modalOpen}
        setIsModalOpen={setModalOpen}
        selectedAction={selectedAction}
        modalDetails={modalDetails}
        specifiedReason={specifiedReason}
        setSpecifiedReason={setSpecifiedReason}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        isReopen={isReopen}
        isIntelApp={cisoDisplay}
        notifyReportingParty={notifyReportingParty}
        setNotifyReportingParty={setNotifyReportingParty}
        assignReopenToSelf={assignReopenToSelf}
        setAssignReopenToSelf={setAssignReopenToSelf}
        emailTemplates={emailTemplates}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        onClose={handleClose}
      />
      <LinkingDialog
        isOpen={isLinkingDialogOpen}
        onClose={() => {
          setIsLinkingDialogOpen(false);
          setSelectedReportToLink(null);
        }}
        selectedReport={selectedReportToLink}
        onConfirm={linkReport}
        appName={appName}
        orgId={orgId ?? ""}
      />
      <SupplementaryReportModal
        isOpen={modalOpenSupplementary}
        onClose={() => setModalOpenSupplementary(false)}
        modalDetails={modalDetails}
        selectedAction={selectedAction}
        rejectButton={rejectButton}
        approveButton={approveButton}
      />
      <NoLinkFoundDialog
        isOpen={isNoLinkFoundDialogOpen}
        onClose={() => {
          setIsNoLinkFoundDialogOpen(false);
          setSelectedReportToLink(null);
        }}
        onConfirm={convertToGeneralReport}
      />
      <EditConfirmationModal
        isOpen={isEditConfirmationModalOpen}
        onClose={handleCancelEditConfirmation}
        onSaveAndContinue={handleSaveAndContinue}
        isSaving={editState?.isSaving ?? false}
      />
      <ConvertToSupplementaryDialog
        isOpen={convertGeneralToSupplementaryDialogOpen}
        onClose={() => setConvertGeneralToSupplementaryDialogOpen(false)}
        onConfirm={convertToSupplementaryReport}
      />
      <UnlinkAllSupplementaryReportsDialog
        isOpen={unlinkAllSupplementaryReportsDialogOpen}
        onClose={() => setUnlinkAllSupplementaryReportsDialogOpen(false)}
        onConfirm={unlinkAllSupplementaryReports}
      />
      <ButtonInfoDialog
        isOpen={isBtnInfoDialogOpen}
        onClose={() => setIsBtnInfoDialogOpen(false)}
        modalDetails={modalDetails}
      />
      <IncidentNumberDialog
        isOpen={incidentNumberDialogOpen}
        onClose={() => {
          setIncidentNumberDialogOpen(false);
        }}
        onContinue={() => {
          setIncidentNumberDialogOpen(false);
          // The close report modal
          setModalOpen(true);
        }}
      />
      <PotentialDuplicateWarningDialog
        open={openDuplicateDialog}
        title={t("duplicate_incident_number_title")}
        message={t("duplicate_incident_number_message")}
        items={duplicateReports.map((report) => ({
          id: report.id,
          href: `/${appName}/${orgId}/report/${report.id}/condensed`,
          label: report.incident_number
            ? `${report.tracking_number ?? report.id} (${report.incident_number})`
            : (report.tracking_number ?? report.id),
        }))}
        cancelLabel={t("duplicate_incident_number_cancel")}
        continueLabel={t("duplicate_incident_number_continue")}
        onCancel={cancelDuplicateSave}
        onClose={cancelDuplicateSave}
        onContinue={() => resolveDuplicateDecision(true)}
      />
      {/* Raised by the router guard whenever unsaved detail edits would be lost
          — the back button, the sidebar, browser back. Keeping the user here is
          the emphasised choice. */}
      <ConfirmDialog
        open={leaveGuard.isBlocked}
        title={t("leave_unsaved_title")}
        message={t("leave_unsaved_description")}
        primaryLabel={t("keep_editing")}
        onPrimary={leaveGuard.reset}
        secondaryLabel={t("leave_without_saving")}
        onSecondary={leaveGuard.proceed}
      />
      {(() => {
        const hasRmsExport =
          !!serviceConfig?.export_configuration?.rms_export_configuration;
        const hasDemsExport =
          !!serviceConfig?.export_configuration?.dems_export_configuration;
        const hasBoth = hasRmsExport && hasDemsExport;

        const descriptionKey = hasBoth
          ? "re_export_modal_description_both"
          : hasDemsExport
            ? "re_export_modal_description_dems"
            : "re_export_modal_description";

        return (
          <Dialog
            open={isReExportModalOpen}
            onClose={() => !reExportLoading && setIsReExportModalOpen(false)}
            aria-labelledby="re-export-dialog-title"
            aria-describedby="re-export-dialog-description"
          >
            <DialogTitle id="re-export-dialog-title">
              {t("re_export_modal_title")}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="re-export-dialog-description">
                {t(descriptionKey)}
              </DialogContentText>
              {hasBoth && (
                <RadioGroup
                  value={reExportOption}
                  onChange={(e) =>
                    setReExportOption(e.target.value as "rms" | "dems" | "both")
                  }
                  sx={{ mt: 2 }}
                >
                  <FormControlLabel
                    value="rms"
                    control={<Radio />}
                    label={t("re_export_option_rms")}
                    disabled={reExportLoading}
                  />
                  <FormControlLabel
                    value="dems"
                    control={<Radio />}
                    label={t("re_export_option_dems")}
                    disabled={reExportLoading}
                  />
                  <FormControlLabel
                    value="both"
                    control={<Radio />}
                    label={t("re_export_option_both")}
                    disabled={reExportLoading}
                  />
                </RadioGroup>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
              <Button
                onClick={() => setIsReExportModalOpen(false)}
                color="secondary"
                variant="outlined"
                disabled={reExportLoading}
              >
                {t("cancel")}
              </Button>
              <LoadingButton
                onClick={handleReExportConfirm}
                color="primary"
                variant="contained"
                loading={reExportLoading}
                disabled={reExportLoading}
              >
                {t("confirm")}
              </LoadingButton>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Concurrent editing warning dialog */}
      <Dialog
        open={concurrentEditDialogState.open}
        onClose={() => {
          setConcurrentEditDialogState({ open: false, names: "" });
          concurrentEditDialogResolveRef.current?.(false);
        }}
        aria-labelledby="concurrent-edit-dialog-title"
        aria-describedby="concurrent-edit-dialog-description"
      >
        <DialogTitle id="concurrent-edit-dialog-title">
          Report Currently Being Edited
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="concurrent-edit-dialog-description">
            <strong>{concurrentEditDialogState.names}</strong>{" "}
            {concurrentEditDialogState.names.includes(",") ? "are" : "is"}{" "}
            currently editing this report. Proceeding may cause conflicting
            changes.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, justifyContent: "space-between" }}>
          <Button
            onClick={() => {
              setConcurrentEditDialogState({ open: false, names: "" });
              concurrentEditDialogResolveRef.current?.(false);
            }}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConcurrentEditDialogState({ open: false, names: "" });
              concurrentEditDialogResolveRef.current?.(true);
            }}
            color="warning"
            variant="contained"
          >
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Notes Button */}
      {/* <FloatingNotesButton
        isVisible={true}
        onClick={() => toggleNotesSidePanel()}
      /> */}

      {/* Notes Side Panel */}
      <NotesSidePanel
        isOpen={isNotesSidePanelOpen}
        onClose={closeNotesSidePanel}
        getCurrentValuesRef={getNotesPanelValuesRef}
        reportMetadata={reportMetadata}
        setReportMetadata={setReportMetadata}
        isClosed={isClosed}
        readOnly={!canUpdateReports}
        modified={modified}
        setModified={setModified}
        updateIncidentNumberAndNotes={updateIncidentNumberAndNotes}
        guardIncidentNumber={guardIncidentNumber}
        revertIncidentNumberRef={notesRevertIncidentNumberRef}
        supplementalDrafts={supplementalDrafts}
        onSupplementalDraftChange={setSupplementalDraft}
        onSupplementalUpdate={applySavedSupplementalNotes}
        onCurrentReportChange={handleCurrentReportChange}
        externalReportIndex={currentReportIndex}
        requireSynopsisVerification={requireSynopsisVerification}
        synopsisVerified={synopsisVerified}
        setSynopsisVerified={setSynopsisVerified}
        serviceConfig={serviceConfig}
        onWidthChange={setNotesPanelWidth}
        onTakeoverChange={setIsPanelTakingOver}
        initialWidth={notesPanelWidth}
        appName={appName}
        orgId={orgId ?? ""}
        activeSidebarTab={notesSidebarTab}
        onActiveSidebarTabChange={setNotesSidebarTab}
      />
    </div>
  );

  return (
    <FilePreviewConfigContext.Provider value={filePreviewConfig}>
      {content}
    </FilePreviewConfigContext.Provider>
  );
};

export default CondensedReportView;
